/**
 * 3ASH DEVICE storefront components.
 *
 * <td-refresh>   re-renders a product page block after a variant change, keeping the
 *                shopper's add-on choices (warranty extension, accessories).
 * <td-buy-now>   puts the device and its selected add-ons in the cart (idempotent), the add-ons nested
 *                under the device's line, then opens checkout directly, or the cart page when the cart
 *                holds other items too.
 * Summary        "Your device" adds the ticked add-ons and the total (updateSummaries).
 * <td-delivery>  shows a Dubai-time delivery estimate with an order cut-off countdown.
 * <td-toast>     newsletter pop-up: waits, never covers an open panel, remembers a dismissal.
 * Panels         any [data-td-open="<id>"] or link to #<id> opens that explanation panel, closing
 *                another open panel first; tabs inside the condition guide; "Choose" buttons in the
 *                guide select that condition; panels close with a downward swipe on phones.
 *                Pages carry the panels' heads only; the content loads once (loadSheets).
 * .td-reveal     one reveal for the home page hero.
 * <td-reviews>   customer reviews row: arrows, and each card opens the full review in a panel.
 * <td-check-cards> what we check, as cards on product pages: each opens the inspection panel
 *                on its own check, which is brought into view and marked for a moment.
 * <td-holo>      hologram stickers (inspection seal, "Expert inspected"): the light on the foil
 *                follows the scroll position, and the pointer on a computer.
 * <td-product-tabs> category tiles that swap the row of devices in place.
 * <td-turns>     words that take turns in one place (model names in the phone search field).
 * <td-rail-box>  a row with its own arrows (shop by series), the current tile brought into view.
 * Search         a suggested search (data-td-search-term) fills the search field and searches.
 */
import { StandardEvents } from '@shopify/events';

const PRODUCT_SELECT = StandardEvents?.productSelect || 'shopify:product:select';

/** @param {Element} element */
function closestScope(element) {
  return element.closest('.shopify-section, dialog');
}

/**
 * Collects add-on variant ids from every form field whose name starts with "td_addon".
 * @param {FormData} formData
 * @returns {string[]}
 */
export function collectAddonIds(formData) {
  const ids = [];
  for (const [key, value] of formData.entries()) {
    if (key.startsWith('td_addon') && typeof value === 'string' && value.trim() !== '') {
      ids.push(value.trim());
    }
  }
  return ids;
}

/**
 * Collects the line item details (fields named properties[Name]), such as the SIM version.
 * @param {FormData} formData
 * @returns {Record<string, string>}
 */
export function collectProperties(formData) {
  /** @type {Record<string, string>} */
  const properties = {};
  for (const [key, value] of formData.entries()) {
    const match = key.match(/^properties\[(.+)\]$/);
    if (match && typeof value === 'string' && value !== '') properties[match[1]] = value;
  }
  return properties;
}

class TdRefresh extends HTMLElement {
  /** @type {Element | null} */
  #scope = null;

  connectedCallback() {
    this.#scope = closestScope(this);
    this.#scope?.addEventListener(PRODUCT_SELECT, this.#onSelect);
  }

  disconnectedCallback() {
    this.#scope?.removeEventListener(PRODUCT_SELECT, this.#onSelect);
    this.#scope = null;
  }

  /** @param {Event & {promise?: Promise<any>}} event */
  #onSelect = (event) => {
    const target = event.target;
    if (target instanceof Element && target.closest('product-card')) return;
    if (!event.promise) return;

    const key = this.dataset.tdKey;
    if (!key) return;

    event.promise
      .then((result) => {
        const html = result?.detail?.html;
        if (!html || !this.isConnected) return;

        const fresh = html.querySelector(`td-refresh[data-td-key="${CSS.escape(key)}"]`);
        if (!fresh) return;

        const chosen = new Set(
          Array.from(this.querySelectorAll('input[data-td-choice]:checked')).map(
            (input) => /** @type {HTMLInputElement} */ (input).dataset.tdChoice
          )
        );

        // Moved, not copied: a copy would start downloading its lazy photos even inside a closed panel
        const clone = /** @type {HTMLElement} */ (document.adoptNode(fresh));

        if (chosen.size > 0) {
          for (const input of clone.querySelectorAll('input[data-td-choice]')) {
            if (!(input instanceof HTMLInputElement)) continue;
            const wanted = chosen.has(input.dataset.tdChoice || '');
            if (input.type === 'checkbox') {
              input.checked = wanted;
            } else if (input.type === 'radio' && wanted) {
              input.checked = true;
            }
          }
        }

        this.replaceWith(clone);
        // The fresh add-ons can carry new prices (the warranty band follows the device price)
        updateSummaries();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[td-refresh]', error);
      });
  };
}

class TdBuyNow extends HTMLElement {
  /** @type {Element | null} */
  #scope = null;
  /** @type {Promise<any> | null} */
  #pending = null;
  #selectFailed = false;
  #busy = false;

  connectedCallback() {
    this.button = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-td-buy-now]'));
    this.errorEl = this.querySelector('[data-td-error]');
    this.button?.addEventListener('click', this.#onClick);
    this.#scope = closestScope(this);
    this.#scope?.addEventListener(PRODUCT_SELECT, this.#onSelect);
    window.addEventListener('pageshow', this.#onPageShow);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.#onClick);
    this.#scope?.removeEventListener(PRODUCT_SELECT, this.#onSelect);
    window.removeEventListener('pageshow', this.#onPageShow);
  }

  #onPageShow = () => {
    this.#setBusy(false);
  };

  /** @param {Event & {promise?: Promise<any>}} event */
  #onSelect = (event) => {
    const target = event.target;
    if (target instanceof Element && target.closest('product-card')) return;
    if (!event.promise) return;

    const promise = event.promise;
    this.#pending = promise;

    promise
      .then((result) => {
        if (this.#pending !== promise) return;
        this.#selectFailed = false;
        if (!this.button) return;
        const resource = result?.detail?.resource;
        // A device without a price yet (0) cannot be bought, when the theme setting asks for it
        const unpriced = this.dataset.zeroGuard === 'true' && resource && Number(resource.price) === 0;
        this.button.disabled = !(resource && resource.available) || Boolean(unpriced);
      })
      .catch((error) => {
        if (this.#pending !== promise) return;
        // An aborted request means a newer selection replaced it; anything else leaves the form stale
        if (error?.name !== 'AbortError') this.#selectFailed = true;
      });
  };

  /** @param {boolean} busy */
  #setBusy(busy) {
    this.#busy = busy;
    if (!this.button) return;
    this.button.setAttribute('aria-busy', String(busy));
    const label = this.button.querySelector('[data-td-label]');
    if (label) {
      label.textContent = busy ? this.dataset.busyText || label.textContent : this.dataset.idleText || label.textContent;
    }
  }

  /** @param {string} message */
  #showError(message) {
    if (this.errorEl) this.errorEl.textContent = message;
  }

  /**
   * A cart request. The routes carry the shopper's language (/ar/cart/add and so on).
   * @param {'add' | 'change' | 'update'} kind
   * @param {object} body
   */
  async #post(kind, body) {
    const root = window.Shopify?.routes?.root || '/';
    const route = window.Theme?.routes?.[`cart_${kind}_url`];
    const url = route ? `${route}.js` : `${root}cart/${kind}.js`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    let data = {};
    try {
      data = await response.json();
    } catch (error) {
      data = {};
    }
    return { ok: response.ok && !data.status, data };
  }

  /** @returns {Promise<{items: any[]}>} */
  async #cart() {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('cart');
    const data = await response.json();
    return { ...data, items: Array.isArray(data.items) ? data.items : [] };
  }

  /**
   * The device's line: this variant at the top level of the cart (not an add-on), with the same details
   * (SIM version) when the page sends some.
   * @param {any[]} items
   * @param {string} id
   * @param {Record<string, string>} properties
   */
  static deviceLine(items, id, properties) {
    const top = items.filter((item) => String(item.variant_id) === id && !item.parent_relationship);
    const same = (/** @type {any} */ item) =>
      Object.entries(properties).every(([key, value]) => (item.properties || {})[key] === value);
    return top.find(same) || top[0] || null;
  }

  /**
   * @param {any} data
   * @returns {Error}
   */
  static cartError(data) {
    const error = new Error('cart');
    /** @type {any} */ (error).description = data?.description || data?.message || '';
    return error;
  }

  /**
   * Puts the device in the cart with exactly the add-ons chosen on the page, nested under its line
   * (Shopify nested cart lines): repeating "buy now" never doubles anything, an add-on that belongs to
   * another device is never touched, and the cart, checkout and order show which device each add-on is for.
   * @param {MouseEvent} event
   */
  #onClick = async (event) => {
    event.preventDefault();
    if (this.#busy || !this.button || this.button.disabled) return;

    const form = /** @type {HTMLFormElement | null} */ (document.getElementById(this.dataset.formId || ''));
    if (!form) return;
    /** @type {HTMLElement | null} */
    const holder = form.closest('product-form-component');

    this.#showError('');
    this.#setBusy(true);

    try {
      if (this.#pending) {
        try {
          await this.#pending;
        } catch (error) {
          /* handled through #selectFailed */
        }
      }
      if (this.#selectFailed) throw new Error('stale-selection');

      const formData = new FormData(form);
      const id = String(formData.get('id') || '').trim();
      if (!id) throw new Error('unavailable');

      const quantity = Math.max(1, Number(formData.get('quantity')) || 1);
      const addonIds = collectAddonIds(formData);
      const properties = collectProperties(formData);
      const hasProperties = Object.keys(properties).length > 0;

      let cart = await this.#cart();
      let line = TdBuyNow.deviceLine(cart.items, id, properties);

      // The per-device limit (theme setting td_max_per_product): every option of this device counts
      const max = Number(holder?.dataset.tdMax) || 0;
      if (max > 0) {
        const productId = String(holder?.dataset.productId || '');
        let inCart = 0;
        for (const item of cart.items) {
          if (String(item.product_id) === productId && !item.parent_relationship) inCart += Number(item.quantity) || 0;
        }
        if (inCart - (line ? line.quantity : 0) + quantity > max) throw new Error('limit');
      }

      if (!line) {
        // The device and its add-ons in one request, the add-ons nested under it
        const device = { id: Number(id), quantity, ...(hasProperties ? { properties } : {}) };
        const addons = addonIds.map((addonId) => ({ id: Number(addonId), quantity, parent_id: Number(id) }));
        let result = await this.#post('add', { items: [device, ...addons] });
        if (!result.ok && addons.length > 0) {
          // An add-on can become unavailable while the page is open: keep the device purchase going
          result = await this.#post('add', { items: [device] });
        }
        if (!result.ok) throw TdBuyNow.cartError(result.data);
      } else {
        // Already in the cart: the quantity and details of this line only
        const same = Object.entries(properties).every(([key, value]) => (line.properties || {})[key] === value);
        if (line.quantity !== quantity || !same) {
          const body = { id: line.key, quantity, ...(hasProperties ? { properties: { ...(line.properties || {}), ...properties } } : {}) };
          const changed = await this.#post('change', body);
          if (!changed.ok) throw TdBuyNow.cartError(changed.data);
          cart = await this.#cart();
          line = TdBuyNow.deviceLine(cart.items, id, properties);
          if (!line) throw new Error('cart');
        }

        // Its add-ons: the ones chosen now, at the same quantity; the others come off this device
        const children = cart.items.filter((item) => item.parent_relationship?.parent_key === line.key);
        /** @type {Record<string, number>} */
        const updates = {};
        const toAdd = [];
        for (const addonId of addonIds) {
          const child = children.find((item) => String(item.variant_id) === addonId);
          if (!child) toAdd.push({ id: Number(addonId), quantity, parent_line_key: line.key });
          else if (child.quantity !== quantity) updates[child.key] = quantity;
        }
        for (const child of children) {
          if (!addonIds.includes(String(child.variant_id))) updates[child.key] = 0;
        }
        if (Object.keys(updates).length > 0) await this.#post('update', { updates });
        // An add-on that cannot be added any more does not stop the purchase
        if (toAdd.length > 0) await this.#post('add', { items: toAdd });
      }

      // Checkout directly, unless the cart holds anything besides this device and its add-ons
      cart = await this.#cart();
      const device = TdBuyNow.deviceLine(cart.items, id, properties);
      const ours = new Set(device ? [device.key] : []);
      for (const item of cart.items) {
        if (device && item.parent_relationship?.parent_key === device.key) ours.add(item.key);
      }
      const hasOtherItems = cart.items.some((item) => !ours.has(item.key));
      const root = window.Shopify?.routes?.root || '/';
      const cartUrl = window.Theme?.routes?.cart_url || `${root}cart`;

      window.location.assign(hasOtherItems ? cartUrl : `${root}checkout`);
    } catch (error) {
      this.#setBusy(false);
      let message = this.dataset.errorText || '';
      if (error instanceof Error && error.message === 'limit') {
        message = holder?.dataset.tdMaxError || message;
      } else if (error instanceof Error && error.message === 'unavailable') {
        message = this.dataset.unavailableText || message;
      } else if (error && /** @type {any} */ (error).description) {
        message = /** @type {any} */ (error).description;
      }
      this.#showError(message);
    }
  };
}

const DUBAI_OFFSET_MS = 4 * 60 * 60 * 1000;

/**
 * @param {Date} date - a Date whose UTC fields hold Dubai wall-clock time
 * @returns {string}
 */
function ymd(date) {
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  return `${date.getUTCFullYear()}-${m}-${d}`;
}

class TdDelivery extends HTMLElement {
  /** @type {number | undefined} */
  #timer;

  connectedCallback() {
    this.#render();
    this.#timer = window.setInterval(() => this.#render(), 30000);
  }

  disconnectedCallback() {
    if (this.#timer) window.clearInterval(this.#timer);
  }

  get #offDays() {
    return new Set(
      (this.dataset.off || '')
        .split(/[,\s]+/)
        .map((value) => value.trim())
        .filter((value) => value !== '' && !Number.isNaN(Number(value)))
        .map(Number)
    );
  }

  get #holidays() {
    return new Set(
      (this.dataset.holidays || '')
        .split(/[,\s]+/)
        .map((value) => value.trim())
        .filter((value) => /^\d{4}-\d{2}-\d{2}$/.test(value))
    );
  }

  /**
   * @param {Date} date
   * @param {Set<number>} off
   * @param {Set<string>} holidays
   */
  #isWorkday(date, off, holidays) {
    return !off.has(date.getUTCDay()) && !holidays.has(ymd(date));
  }

  /**
   * @param {Date} start
   * @param {number} days
   * @param {Set<number>} off
   * @param {Set<string>} holidays
   */
  #addWorkdays(start, days, off, holidays) {
    const date = new Date(start.getTime());
    let added = 0;
    let guard = 0;
    while (added < days && guard < 60) {
      date.setUTCDate(date.getUTCDate() + 1);
      guard += 1;
      if (this.#isWorkday(date, off, holidays)) added += 1;
    }
    return date;
  }

  /** @param {Date} date */
  #formatDate(date) {
    const lang = (this.dataset.locale || document.documentElement.lang || 'en').toLowerCase();
    const locale = lang.startsWith('ar') ? 'ar-AE-u-nu-latn' : 'en-AE';
    try {
      return new Intl.DateTimeFormat(locale, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        timeZone: 'UTC',
      }).format(date);
    } catch (error) {
      return ymd(date);
    }
  }

  #render() {
    const titleEl = this.querySelector('[data-td-title]');
    const subEl = this.querySelector('[data-td-sub]');
    if (!titleEl) return;

    const off = this.#offDays;
    const holidays = this.#holidays;
    if (off.size >= 7) return;

    const cutoff = Math.min(23, Math.max(0, Number(this.dataset.cutoff ?? 16)));
    const now = new Date(Date.now() + DUBAI_OFFSET_MS);
    const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));

    const minutesNow = now.getUTCHours() * 60 + now.getUTCMinutes();
    const beforeCutoff = minutesNow < cutoff * 60;
    const shipsToday = this.#isWorkday(today, off, holidays) && beforeCutoff;

    let dispatch = today;
    if (!shipsToday) dispatch = this.#addWorkdays(today, 1, off, holidays);

    const mode = this.dataset.mode || 'stock';

    if (mode === 'network') {
      const min = Math.max(0, Number(this.dataset.daysMin ?? 2));
      const max = Math.max(min, Number(this.dataset.daysMax ?? 4));
      const from = this.#addWorkdays(dispatch, min, off, holidays);
      const to = this.#addWorkdays(dispatch, max, off, holidays);
      const template = this.dataset.tRange || '';
      if (template) {
        titleEl.textContent =
          min === max
            ? (this.dataset.tArrives || '').replace('[date]', this.#formatDate(from))
            : template.replace('[from]', this.#formatDate(from)).replace('[to]', this.#formatDate(to));
      }
      return;
    }

    const days = Math.max(0, Number(this.dataset.daysStock ?? 1));
    const arrival = days === 0 ? dispatch : this.#addWorkdays(dispatch, days, off, holidays);
    const dateText = this.#formatDate(arrival);

    if (shipsToday) {
      const remaining = cutoff * 60 - minutesNow;
      const hours = Math.floor(remaining / 60);
      const minutes = remaining % 60;
      const timeText =
        hours > 0
          ? (this.dataset.tHm || '[h] h [m] min').replace('[h]', String(hours)).replace('[m]', String(minutes))
          : (this.dataset.tM || '[m] min').replace('[m]', String(Math.max(1, minutes)));
      const template = this.dataset.tWithin || '';
      if (template) {
        titleEl.innerHTML = '';
        const [before, after] = template.split('[time]');
        titleEl.append(document.createTextNode(before || ''));
        const strong = document.createElement('span');
        strong.className = 'td-delivery__countdown';
        strong.textContent = timeText;
        titleEl.append(strong);
        titleEl.append(document.createTextNode((after || '').replace('[date]', dateText)));
      }
    } else if (this.dataset.tArrives) {
      titleEl.textContent = this.dataset.tArrives.replace('[date]', dateText);
    }

    if (subEl && this.dataset.sub) subEl.textContent = this.dataset.sub;
  }
}

// ---------------------------------------------------------------------------
// "Your device" summary: the add-ons ticked on the page (warranty extension, accessories) and the
// total, from the prices on the add-on choices (block td-addons, data-td-price)
// ---------------------------------------------------------------------------

function updateSummaries() {
  for (const box of document.querySelectorAll('[data-td-summary-adds]')) {
    if (!(box instanceof HTMLElement)) continue;
    const formId = box.dataset.formId || '';
    const format = box.dataset.moneyFormat || '';
    const trim = box.dataset.trim === 'true';
    let total = Number(box.dataset.devicePrice || 0);
    /** @type {HTMLLIElement[]} */
    const lines = [];
    for (const input of document.querySelectorAll(`input[form="${CSS.escape(formId)}"][name^="td_addon"]:checked`)) {
      if (!(input instanceof HTMLInputElement) || !input.value) continue;
      const price = Number(input.dataset.tdPrice || 0);
      if (price <= 0) continue;
      total += price;
      const item = document.createElement('li');
      const name = document.createElement('span');
      name.textContent = input.dataset.tdTitle || '';
      const amount = document.createElement('span');
      const money = document.createElement('bdi');
      money.textContent = formatMoney(price, format, trim);
      amount.append('+\u00a0', money);
      item.append(name, amount);
      lines.push(item);
    }
    box.querySelector('[data-td-summary-lines]')?.replaceChildren(...lines);
    const totalEl = box.querySelector('[data-td-summary-total]');
    if (totalEl) totalEl.textContent = formatMoney(total, format, trim);
    box.hidden = lines.length === 0;
  }
}

document.addEventListener('change', (event) => {
  const input = event.target instanceof HTMLInputElement ? event.target : null;
  if (input && input.name.startsWith('td_addon')) updateSummaries();
});

// ---------------------------------------------------------------------------
// The address keeps the chosen option (?variant=), so a shared link or a reload shows the same device
// and price. Main product only: not product cards, quick view or other dialogs.
// ---------------------------------------------------------------------------

/**
 * @param {Element | null} scope
 * @param {any} resource - the selected variant
 */
function rememberVariant(scope, resource) {
  if (!scope || !resource?.id || scope.closest('dialog')) return;
  if (!/\/products\//.test(window.location.pathname)) return;
  if (!scope.querySelector('product-form-component')) return;
  const url = new URL(window.location.href);
  if (url.searchParams.get('variant') === String(resource.id)) return;
  url.searchParams.set('variant', String(resource.id));
  try {
    window.history.replaceState(window.history.state, '', url.toString());
  } catch (error) {
    /* the address simply stays as it was */
  }
}

// ---------------------------------------------------------------------------
// Explanation panels
// ---------------------------------------------------------------------------

/**
 * @param {string} id
 * @returns {(HTMLElement & {showDialog: () => void, closeDialog: () => Promise<void>, refs?: any}) | null}
 */
function panelHost(id) {
  let host = document.getElementById(id);
  // The general condition guide gives way to the product page's own guide (prices, "Choose")
  if (id === 'td-sheet-grades') {
    const productGuide = document.querySelector('dialog-component[data-td-product-guide]');
    if (productGuide) host = /** @type {HTMLElement} */ (productGuide);
  }
  if (!host || host.tagName !== 'DIALOG-COMPONENT') return null;
  return /** @type {any} */ (host);
}

/** @returns {any} */
function openPanelHost() {
  const open = document.querySelector('dialog-component > dialog.td-drawer[open]');
  return open ? open.parentElement : null;
}

// ---------------------------------------------------------------------------
// The panels arrive in full on first use. Pages carry only their heads (data-td-lazy); section td-sheets
// brings the rest, fetched once while the page is idle, or as soon as the shopper reaches for something
// that opens a panel (pointer over it, focus or touch).
// ---------------------------------------------------------------------------

/** @type {Promise<boolean> | null} */
let sheetsLoad = null;

/** @returns {Promise<boolean>} whether the panels are in place */
function loadSheets() {
  if (!document.querySelector('dialog-component[data-td-lazy]')) return Promise.resolve(true);
  // An iPad page (data-td-sheets-here) asks its own address, so the panels carry the iPad's lines (31 Oct 2026)
  const from = document.querySelector('[data-td-sheets-here]') ? location.pathname : window.Shopify?.routes?.root || '/';
  sheetsLoad ||= fetch(`${from}?section_id=td-sheets`, { credentials: 'same-origin' })
    .then((response) => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.text();
    })
    .then((html) => {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      for (const shell of document.querySelectorAll('dialog-component[data-td-lazy]')) {
        // A head that is open (only after a failed load) stays until it closes
        if (shell.querySelector('dialog[open]')) continue;
        const full = doc.getElementById(shell.id);
        // Moved, not copied: a copy would start downloading its lazy photos even inside a closed panel
        if (full && full.tagName === 'DIALOG-COMPONENT') shell.replaceWith(document.adoptNode(full));
      }
      return true;
    })
    .catch((error) => {
      // The next panel the shopper opens tries again
      sheetsLoad = null;
      console.warn('[td-sheets]', error);
      return false;
    });
  return sheetsLoad;
}

function prefetchSheets() {
  if (!sheetsLoad && document.querySelector('dialog-component[data-td-lazy]')) loadSheets();
}

for (const type of ['pointerover', 'focusin', 'touchstart']) {
  document.addEventListener(
    type,
    (event) => {
      if (sheetsLoad || !(event.target instanceof Element)) return;
      if (event.target.closest('[data-td-open], a[href*="#td-sheet-"]')) prefetchSheets();
    },
    { passive: true }
  );
}

function prefetchSheetsWhenIdle() {
  const start = () => {
    if ('requestIdleCallback' in window) window.requestIdleCallback(prefetchSheets, { timeout: 4000 });
    else window.setTimeout(prefetchSheets, 2500);
  };
  if (document.readyState === 'complete') start();
  else window.addEventListener('load', start, { once: true });
}

/**
 * Opens a panel; an open panel closes first so they never stack, and so do the search window and the
 * phone menu (their help links open panels). A panel that has only its head loads first.
 * @param {string} id
 */
async function openPanel(id) {
  let host = panelHost(id);
  if (!host || typeof host.showDialog !== 'function') return false;
  if (host.hasAttribute('data-td-lazy')) {
    await loadSheets();
    host = panelHost(id) || host;
  }
  const current = openPanelHost();
  if (current === host) return true;
  if (current && typeof current.closeDialog === 'function') await current.closeDialog();
  const search = /** @type {any} */ (document.getElementById('search-modal'));
  if (search?.refs?.dialog?.open && typeof search.closeDialog === 'function') await search.closeDialog();
  // The phone menu's help rows open panels too: the menu closes first
  for (const drawer of document.querySelectorAll('header-drawer')) {
    const menu = /** @type {any} */ (drawer);
    if (menu.refs?.details?.open && typeof menu.close === 'function') menu.close();
  }
  host.showDialog();
  return true;
}

document.addEventListener('click', (event) => {
  const target = /** @type {Element | null} */ (event.target instanceof Element ? event.target : null);
  if (!target) return;

  // The language switch opens the same page in the other language with the same option, filters and search
  const lang = target.closest('a.td-lang');
  if (lang instanceof HTMLAnchorElement) {
    const url = new URL(lang.href, window.location.href);
    if (!url.search && window.location.search) url.search = window.location.search;
    if (!url.hash && window.location.hash) url.hash = window.location.hash;
    lang.href = url.toString();
    return;
  }

  const trigger = target.closest('[data-td-open], a[href*="#td-sheet-"]');
  if (trigger) {
    let id = trigger.getAttribute('data-td-open') || '';
    if (!id) {
      const href = trigger.getAttribute('href') || '';
      const url = new URL(href, window.location.href);
      if (url.pathname !== window.location.pathname) return;
      id = url.hash.slice(1);
    }
    if (id && panelHost(id)) {
      event.preventDefault();
      const shot = Number(trigger.getAttribute('data-td-shot') || 0);
      const check = Number(trigger.getAttribute('data-td-check') || 0);
      trigger.setAttribute('aria-busy', 'true');
      openPanel(id).then((opened) => {
        trigger.removeAttribute('aria-busy');
        if (opened && shot > 0) requestAnimationFrame(() => showGuidePhoto(id, shot));
        if (opened && check > 0) setTimeout(() => showCheck(id, check), 80);
      });
    }
    return;
  }

  // A suggested search fills the search field and shows its results in place
  const term = target.closest('[data-td-search-term]');
  if (term instanceof HTMLElement) {
    const input = term.closest('predictive-search-component')?.querySelector('input[type="search"]');
    if (input instanceof HTMLInputElement) {
      event.preventDefault();
      const value = term.dataset.tdSearchTerm || '';
      input.value = value;
      input.dispatchEvent(new InputEvent('input', { bubbles: true, inputType: 'insertText', data: value }));
      // Phones keep the keyboard closed so the results stay in view
      if (window.matchMedia('(pointer: fine)').matches) input.focus();
    }
    return;
  }

  const tab = target.closest('[data-td-tab]');
  if (tab instanceof HTMLElement) {
    selectTab(tab);
    return;
  }

  const step = target.closest('[data-td-gallery-step]');
  if (step instanceof HTMLElement) {
    const gallery = step.closest('[data-td-gallery]');
    const track = gallery?.querySelector('[data-td-gallery-track]');
    if (gallery instanceof HTMLElement && track instanceof HTMLElement) {
      galleryGo(gallery, galleryIndex(track) + Number(step.dataset.tdGalleryStep || 0));
    }
    return;
  }

  const zoom = target.closest('[data-td-zoom]');
  if (zoom instanceof HTMLElement) {
    openZoom(zoom);
    return;
  }

  const zoomLayer = target.closest('[data-td-zoom-layer]');
  if (zoomLayer instanceof HTMLElement) {
    closeZoom(zoomLayer);
    return;
  }

  const sizeCompare = target.closest('[data-td-size-compare]');
  if (sizeCompare instanceof HTMLElement) {
    openSizeFor(sizeCompare.dataset.tdSizeCompare || '');
    return;
  }

  const tradeinNo = target.closest('[data-td-tradein-no]');
  if (tradeinNo instanceof HTMLElement) {
    putTradeinAway(tradeinNo.closest('[data-td-tradein]'));
    return;
  }

  const choose = target.closest('[data-td-choose]');
  if (choose instanceof HTMLElement) chooseCondition(choose);
});

// ---------------------------------------------------------------------------
// Condition photos: a swipeable row in the guide (arrows, "1 / 3"), opened on the photo the
// shopper tapped under the condition tiles, and a full-size view inside the panel
// ---------------------------------------------------------------------------

/** @param {HTMLElement} track */
function galleryIndex(track) {
  const slide = track.querySelector('.td-gallery__slide');
  const width = slide instanceof HTMLElement ? slide.offsetWidth : track.clientWidth;
  return width ? Math.round(Math.abs(track.scrollLeft) / width) : 0;
}

/** @param {Element} gallery */
function galleryUpdate(gallery) {
  const track = gallery.querySelector('[data-td-gallery-track]');
  if (!(track instanceof HTMLElement)) return;
  const total = track.children.length;
  const index = Math.max(0, Math.min(total - 1, galleryIndex(track)));
  const count = gallery.querySelector('[data-td-gallery-count]');
  if (count) count.textContent = `${index + 1} / ${total}`;
  const prev = gallery.querySelector('[data-td-gallery-step="-1"]');
  const next = gallery.querySelector('[data-td-gallery-step="1"]');
  if (prev instanceof HTMLButtonElement) prev.disabled = index <= 0;
  if (next instanceof HTMLButtonElement) next.disabled = index >= total - 1;
}

/**
 * @param {Element} gallery
 * @param {number} index - 0-based photo to show
 * @param {boolean} [smooth]
 */
function galleryGo(gallery, index, smooth = true) {
  const track = gallery.querySelector('[data-td-gallery-track]');
  if (!(track instanceof HTMLElement)) return;
  const total = track.children.length;
  const to = Math.max(0, Math.min(total - 1, index));
  const slide = track.children[to];
  if (!(slide instanceof HTMLElement)) return;
  const rtl = getComputedStyle(track).direction === 'rtl';
  track.scrollTo({
    left: slide.offsetWidth * to * (rtl ? -1 : 1),
    behavior: smooth && !reducedMotion.matches ? 'smooth' : 'auto',
  });
  if (!smooth || reducedMotion.matches) galleryUpdate(gallery);
}

/**
 * Shows photo n (1-based) of the open tab in a condition guide.
 * @param {string} id
 * @param {number} shot
 */
function showGuidePhoto(id, shot) {
  const host = panelHost(id);
  const gallery = host?.querySelector('.td-tabs__panel:not([hidden]) [data-td-gallery]');
  if (gallery) galleryGo(gallery, shot - 1, false);
}

/**
 * Brings check n (1-based) of the inspection panel into view and marks it for a moment, for the
 * check card the shopper tapped.
 * @param {string} id
 * @param {number} check
 */
function showCheck(id, check) {
  const host = panelHost(id);
  const line = host?.querySelector(`[data-td-check-line="${check}"]`);
  if (!host || !(line instanceof HTMLElement)) return;
  line.scrollIntoView({ block: 'center', behavior: reducedMotion.matches ? 'auto' : 'smooth' });
  host.querySelectorAll('[data-td-check-line].is-focus').forEach((marked) => marked.classList.remove('is-focus'));
  void line.offsetWidth;
  line.classList.add('is-focus');
  line.addEventListener('animationend', () => line.classList.remove('is-focus'), { once: true });
}

document.addEventListener(
  'scroll',
  (event) => {
    const track = event.target;
    if (!(track instanceof HTMLElement) || !track.matches('[data-td-gallery-track]')) return;
    const gallery = track.closest('[data-td-gallery]');
    if (gallery) requestAnimationFrame(() => galleryUpdate(gallery));
  },
  { capture: true, passive: true }
);

/** @type {HTMLElement | null} */
let zoomReturn = null;

/** @param {HTMLElement} button */
function openZoom(button) {
  const layer = button.closest('dialog')?.querySelector('[data-td-zoom-layer]');
  const frame = layer?.querySelector('[data-td-zoom-frame]');
  if (!(layer instanceof HTMLElement) || !frame) return;
  let image = frame.querySelector('img');
  if (!image) {
    image = document.createElement('img');
    image.className = 'td-zoom__img';
    image.decoding = 'async';
    frame.append(image);
  }
  image.src = button.dataset.tdZoom || '';
  image.alt = button.dataset.tdZoomAlt || '';
  layer.hidden = false;
  // In the top layer the photo fills the whole screen; without popover support it fills the panel
  if (typeof layer.showPopover === 'function' && !layer.matches(':popover-open')) {
    try {
      layer.showPopover();
    } catch (error) {
      // already shown or not connected: the panel-sized view still works
    }
  }
  zoomReturn = button;
  const close = layer.querySelector('[data-td-zoom-close]');
  if (close instanceof HTMLElement) close.focus();
}

/** @param {Element | null} layer */
function closeZoom(layer) {
  if (!(layer instanceof HTMLElement) || layer.hidden) return;
  if (typeof layer.hidePopover === 'function' && layer.matches(':popover-open')) layer.hidePopover();
  layer.hidden = true;
  zoomReturn?.focus();
  zoomReturn = null;
}

// Escape closes the full-size photo first, and only then the panel
window.addEventListener(
  'keydown',
  (event) => {
    if (event.key !== 'Escape') return;
    const layer = document.querySelector('[data-td-zoom-layer]:not([hidden])');
    if (!layer) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    closeZoom(layer);
  },
  true
);
document.addEventListener(
  'cancel',
  (event) => {
    if (document.querySelector('[data-td-zoom-layer]:not([hidden])')) event.preventDefault();
  },
  true
);
document.addEventListener(
  'dialog:close',
  (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const layer = target?.querySelector('[data-td-zoom-layer]:not([hidden])');
    if (layer instanceof HTMLElement) {
      zoomReturn = null;
      closeZoom(layer);
    }
  },
  true
);

// ---------------------------------------------------------------------------
// "Have an old device?": "No" puts the card away for the rest of the visit
// ---------------------------------------------------------------------------

const TRADEIN_KEY = 'td-tradein-no';

/** @param {Element | null} card */
function putTradeinAway(card) {
  try {
    sessionStorage.setItem(TRADEIN_KEY, '1');
  } catch (error) {
    // private mode: the card still goes away on this page
  }
  if (!(card instanceof HTMLElement)) return;
  if (reducedMotion.matches || typeof card.animate !== 'function') {
    card.hidden = true;
    return;
  }
  const height = card.offsetHeight;
  card
    .animate(
      [
        { opacity: 1, height: `${height}px` },
        { opacity: 0, height: '0px', marginBlock: '0px', paddingBlock: '0px' },
      ],
      { duration: 280, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' }
    )
    .finished.then(() => {
      card.hidden = true;
    })
    .catch(() => {
      card.hidden = true;
    });
}

/** The chat button sits above the sticky buy bar: its height goes to --td-bar-h whenever it changes */
function watchBuyBar() {
  const bar = document.querySelector('.sticky-add-to-cart__bar');
  if (!(bar instanceof HTMLElement)) return;
  const update = () => {
    const height = Math.round(bar.getBoundingClientRect().height);
    if (height > 0) document.documentElement.style.setProperty('--td-bar-h', `${height}px`);
  };
  new MutationObserver(update).observe(bar, { attributes: true, attributeFilter: ['data-stuck'] });
  if ('ResizeObserver' in window) new ResizeObserver(update).observe(bar);
  update();
}

function hideTradeinIfDeclined() {
  let declined = false;
  try {
    declined = sessionStorage.getItem(TRADEIN_KEY) === '1';
  } catch (error) {
    declined = false;
  }
  if (!declined) return;
  for (const card of document.querySelectorAll('[data-td-tradein]')) {
    if (card instanceof HTMLElement) card.hidden = true;
  }
}

// ---------------------------------------------------------------------------
// Tabs (condition guide)
// ---------------------------------------------------------------------------

/** @param {HTMLElement} tab */
function selectTab(tab) {
  const list = tab.closest('[role="tablist"]');
  const root = tab.closest('[data-td-tabs]');
  if (!list || !root || tab.getAttribute('aria-selected') === 'true') return;
  for (const other of list.querySelectorAll('[role="tab"]')) {
    const selected = other === tab;
    other.setAttribute('aria-selected', String(selected));
    other.setAttribute('tabindex', selected ? '0' : '-1');
    const panel = document.getElementById(other.getAttribute('aria-controls') || '');
    if (panel) panel.hidden = !selected;
  }
}

document.addEventListener('keydown', (event) => {
  const tab = event.target instanceof HTMLElement ? event.target.closest('[role="tab"][data-td-tab]') : null;
  if (!(tab instanceof HTMLElement)) return;
  const list = tab.closest('[role="tablist"]');
  if (!list) return;
  const tabs = /** @type {HTMLElement[]} */ (Array.from(list.querySelectorAll('[role="tab"]')));
  const index = tabs.indexOf(tab);
  const rtl = getComputedStyle(list).direction === 'rtl';
  let next = -1;
  if (event.key === 'ArrowRight') next = rtl ? index - 1 : index + 1;
  else if (event.key === 'ArrowLeft') next = rtl ? index + 1 : index - 1;
  else if (event.key === 'Home') next = 0;
  else if (event.key === 'End') next = tabs.length - 1;
  else return;
  event.preventDefault();
  const target = tabs[(next + tabs.length) % tabs.length];
  if (!target) return;
  selectTab(target);
  target.focus();
});

/**
 * Selects a condition in the variant picker from the guide, after the guide has closed
 * (the guide is re-rendered with the new variant, so it must not be open while that happens).
 * @param {HTMLElement} button
 */
async function chooseCondition(button) {
  const value = button.dataset.tdChoose || '';
  const picker = document.getElementById(button.dataset.tdPicker || '');
  const host = /** @type {any} */ (button.closest('dialog-component'));
  if (host && typeof host.closeDialog === 'function') await host.closeDialog();
  if (!picker) return;
  const inputs = picker.querySelectorAll('.td-vp__option--condition input[type="radio"]');
  for (const input of inputs) {
    if (input instanceof HTMLInputElement && input.value === value) {
      if (!input.checked) input.click();
      input.closest('fieldset')?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      break;
    }
  }
}

// ---------------------------------------------------------------------------
// Phones: swipe a panel down to close it
// ---------------------------------------------------------------------------

/** @type {{dialog: HTMLDialogElement, startY: number, dy: number, t: number, id: number} | null} */
let swipe = null;
const phone = window.matchMedia('(max-width: 749px)');

document.addEventListener(
  'pointerdown',
  (event) => {
    if (event.pointerType !== 'touch' || !phone.matches) return;
    const target = event.target instanceof Element ? event.target : null;
    const head = target?.closest('.td-drawer__head');
    if (!head || target?.closest('button, a')) return;
    const dialog = head.closest('dialog.td-drawer');
    if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return;
    swipe = { dialog, startY: event.clientY, dy: 0, t: performance.now(), id: event.pointerId };
  },
  { passive: true }
);

document.addEventListener(
  'pointermove',
  (event) => {
    if (!swipe || event.pointerId !== swipe.id) return;
    swipe.dy = Math.max(0, event.clientY - swipe.startY);
    swipe.dialog.style.translate = `0 ${swipe.dy}px`;
  },
  { passive: true }
);

/** @param {PointerEvent} event */
function endSwipe(event) {
  if (!swipe || event.pointerId !== swipe.id) return;
  const { dialog, dy, t } = swipe;
  swipe = null;
  const velocity = dy / Math.max(1, performance.now() - t);
  if (dy > 110 || (dy > 30 && velocity > 0.5)) {
    const host = /** @type {any} */ (dialog.parentElement);
    dialog.style.setProperty('--start-y', `${dy}px`);
    dialog.style.translate = '';
    host?.addEventListener('dialog:close', () => dialog.style.removeProperty('--start-y'), { once: true });
    host?.closeDialog?.();
  } else {
    dialog.style.transition = 'translate 0.25s cubic-bezier(0.32, 0.72, 0, 1)';
    dialog.style.translate = '';
    window.setTimeout(() => {
      dialog.style.transition = '';
    }, 260);
  }
}

document.addEventListener('pointerup', endSwipe);
document.addEventListener('pointercancel', endSwipe);

// A link such as /products/iphone-15#td-sheet-returns opens that panel on arrival
function openPanelFromHash() {
  const id = window.location.hash.slice(1);
  if (!id.startsWith('td-sheet-')) return;
  window.setTimeout(() => openPanel(id), 300);
}

// ---------------------------------------------------------------------------
// Newsletter pop-up
// ---------------------------------------------------------------------------

const TOAST_KEY = 'td-newsletter';
const TOAST_REST_MS = 30 * 24 * 60 * 60 * 1000;

/** @returns {{subscribed?: boolean, dismissed?: number}} */
function readToastState() {
  try {
    return JSON.parse(window.localStorage.getItem(TOAST_KEY) || '{}') || {};
  } catch (error) {
    return {};
  }
}

/** @param {{subscribed?: boolean, dismissed?: number}} state */
function writeToastState(state) {
  try {
    window.localStorage.setItem(TOAST_KEY, JSON.stringify(state));
  } catch (error) {
    /* storage can be unavailable; the pop-up then simply shows again on a later visit */
  }
}

class TdToast extends HTMLElement {
  /** @type {number | undefined} */
  #timer;

  connectedCallback() {
    const params = new URLSearchParams(window.location.search);
    const fromThisForm = window.location.hash === '#td-newsletter';
    const state = readToastState();

    if (params.get('customer_posted') === 'true' || this.dataset.state === 'posted') {
      writeToastState({ ...state, subscribed: true });
      if (fromThisForm) this.#show();
      return;
    }
    if (this.dataset.state === 'error' && fromThisForm) {
      this.#show();
      return;
    }
    if (state.subscribed) return;
    if (state.dismissed && Date.now() - state.dismissed < TOAST_REST_MS) return;

    const delay = Math.max(3, Number(this.dataset.delay) || 20) * 1000;
    this.#timer = window.setTimeout(() => this.#tryShow(), delay);
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);
  }

  disconnectedCallback() {
    if (this.#timer) window.clearTimeout(this.#timer);
    document.documentElement.classList.remove('td-toast-open');
  }

  #tryShow() {
    // Never on top of an open panel, menu or cart: try again a little later
    if (document.querySelector('dialog[open]')) {
      this.#timer = window.setTimeout(() => this.#tryShow(), 6000);
      return;
    }
    this.#show();
  }

  #show() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);
    this.hidden = false;
    document.documentElement.classList.add('td-toast-open');
    window.requestAnimationFrame(() => window.requestAnimationFrame(() => this.classList.add('is-in')));
  }

  #hide() {
    this.classList.remove('is-in');
    document.documentElement.classList.remove('td-toast-open');
    window.setTimeout(() => {
      this.hidden = true;
    }, 360);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target?.closest('[data-td-toast-close]')) return;
    const state = readToastState();
    if (!state.subscribed) writeToastState({ ...state, dismissed: Date.now() });
    this.#hide();
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key !== 'Escape') return;
    const state = readToastState();
    if (!state.subscribed) writeToastState({ ...state, dismissed: Date.now() });
    this.#hide();
  };
}

function initReveal() {
  const elements = Array.from(document.querySelectorAll('.td-reveal'));
  if (elements.length === 0) return;
  if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    elements.forEach((element) => element.classList.add('is-visible'));
    return;
  }

  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  elements.forEach((element) => {
    if (element.getBoundingClientRect().top < viewportHeight) element.classList.add('is-visible');
  });

  document.documentElement.classList.add('td-js');

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      }
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
  );

  elements.forEach((element) => {
    if (!element.classList.contains('is-visible')) observer.observe(element);
  });
}

// ---------------------------------------------------------------------------
// Slideshow (hero and image banners)
// ---------------------------------------------------------------------------

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

class TdSlideshow extends HTMLElement {
  #index = 0;
  /** @type {number | undefined} */
  #timer;
  /** User pressed pause */
  #paused = false;
  /** Reasons to hold autoplay for now: hover, focus, hidden tab, off screen, theme editor */
  #holds = new Set();
  /** @type {HTMLElement[]} */
  #slides = [];
  /** @type {HTMLElement[]} */
  #dots = [];
  /** @type {HTMLElement | null} */
  #status = null;
  /** @type {IntersectionObserver | null} */
  #observer = null;
  /** @type {{x: number, y: number, id: number, moved: boolean} | null} */
  #touch = null;
  #swiped = false;
  #abort = new AbortController();

  connectedCallback() {
    this.#slides = /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('.td-ss__slide')));
    this.#dots = /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[data-td-ss-go]')));
    this.#status = this.querySelector('[data-td-ss-status]');
    if (this.#slides.length < 2) return;

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('click', this.#onClickCapture, { signal, capture: true });
    this.addEventListener('keydown', this.#onKeydown, { signal });
    this.addEventListener('pointerenter', (event) => {
      if (event.pointerType === 'mouse') this.#hold('hover');
    }, { signal });
    this.addEventListener('pointerleave', () => this.#release('hover'), { signal });
    this.addEventListener('focusin', () => this.#hold('focus'), { signal });
    this.addEventListener('focusout', (event) => {
      if (!(event.relatedTarget instanceof Node) || !this.contains(event.relatedTarget)) this.#release('focus');
    }, { signal });

    const viewport = this.querySelector('.td-ss__viewport');
    viewport?.addEventListener('pointerdown', this.#onPointerDown, { signal, passive: true });
    viewport?.addEventListener('pointermove', this.#onPointerMove, { signal, passive: true });
    viewport?.addEventListener('pointerup', this.#onPointerUp, { signal });
    viewport?.addEventListener('pointercancel', () => (this.#touch = null), { signal });

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.#hold('hidden');
      else this.#release('hidden');
    }, { signal });
    reducedMotion.addEventListener('change', () => this.#schedule(), { signal });

    document.addEventListener('shopify:block:select', (event) => {
      const block = event.target instanceof Element ? event.target.closest('.td-ss__slide') : null;
      if (!block || !this.contains(block)) return;
      this.#hold('editor');
      this.#go(Number(/** @type {HTMLElement} */ (block).dataset.index) || 0, false);
    }, { signal });
    document.addEventListener('shopify:block:deselect', () => this.#release('editor'), { signal });

    // Start held when the page opens in a background tab, and until the observer has said the
    // slideshow is on screen, so slides never advance where nobody can see them.
    if (document.hidden) this.#holds.add('hidden');
    if ('IntersectionObserver' in window) {
      this.#holds.add('offscreen');
      this.#observer = new IntersectionObserver(([entry]) => {
        if (entry?.isIntersecting) this.#release('offscreen');
        else this.#hold('offscreen');
      });
      this.#observer.observe(this);
    }

    this.#go(0, false);
  }

  disconnectedCallback() {
    window.clearTimeout(this.#timer);
    this.#abort.abort();
    this.#observer?.disconnect();
    this.#observer = null;
  }

  get #autoplayMs() {
    return reducedMotion.matches ? 0 : Math.max(0, Number(this.dataset.autoplay) || 0);
  }

  get #rtl() {
    return getComputedStyle(this).direction === 'rtl';
  }

  /** @param {string} reason */
  #hold(reason) {
    this.#holds.add(reason);
    this.#schedule();
  }

  /** @param {string} reason */
  #release(reason) {
    if (!this.#holds.delete(reason)) return;
    this.#schedule();
  }

  /**
   * @param {number} target
   * @param {boolean} announce - say the new slide to screen readers (user actions only)
   */
  #go(target, announce) {
    const count = this.#slides.length;
    const index = ((target % count) + count) % count;
    const changed = index !== this.#index;
    this.#index = index;

    this.#slides.forEach((slide, i) => {
      const active = i === index;
      slide.classList.toggle('is-active', active);
      if (active) {
        slide.removeAttribute('aria-hidden');
        slide.inert = false;
        if (changed) {
          slide.classList.remove('is-entering');
          void slide.offsetWidth;
          slide.classList.add('is-entering');
        }
      } else {
        slide.setAttribute('aria-hidden', 'true');
        slide.inert = true;
        slide.classList.remove('is-entering');
      }
    });
    this.#dots.forEach((dot, i) => {
      if (i === index) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
    this.style.setProperty('--td-ss-index', String(index));
    if (announce && this.#status) this.#status.textContent = this.#slides[index]?.getAttribute('aria-label') || '';
    this.#schedule();
  }

  #schedule() {
    window.clearTimeout(this.#timer);
    const ms = this.#autoplayMs;
    const running = ms > 0 && !this.#paused && this.#holds.size === 0;
    this.style.setProperty('--td-ss-ms', `${ms}ms`);
    // Restart the progress line of the current dot
    this.classList.remove('is-playing');
    if (running) {
      void this.offsetWidth;
      this.classList.add('is-playing');
      this.#timer = window.setTimeout(() => this.#go(this.#index + 1, false), ms);
    }
    this.classList.toggle('is-paused', this.#paused);
    const toggle = this.querySelector('[data-td-ss-toggle]');
    if (toggle) {
      const label = this.#paused || ms === 0 ? this.dataset.labelPlay : this.dataset.labelPause;
      if (label) toggle.setAttribute('aria-label', label);
      toggle.toggleAttribute('hidden', reducedMotion.matches);
    }
    // Dots only: the current dot is named for what tapping it does (pause or play)
    if (this.hasAttribute('data-td-ss-dot-pause')) {
      this.#dots.forEach((dot, i) => {
        if (dot.dataset.tdSsLabel === undefined) dot.dataset.tdSsLabel = dot.getAttribute('aria-label') || '';
        let label = dot.dataset.tdSsLabel;
        if (i === this.#index && ms > 0) label = (this.#paused ? this.dataset.labelPlay : this.dataset.labelPause) || label;
        dot.setAttribute('aria-label', label);
      });
    }
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const dot = target.closest('[data-td-ss-go]');
    if (dot instanceof HTMLElement) {
      const to = Number(dot.dataset.tdSsGo) || 0;
      // Dots only: the current dot pauses and resumes the slideshow
      if (to === this.#index && this.hasAttribute('data-td-ss-dot-pause') && this.#autoplayMs > 0) {
        this.#paused = !this.#paused;
        this.#schedule();
        return;
      }
      this.#go(to, true);
      return;
    }
    if (target.closest('[data-td-ss-prev]')) {
      this.#go(this.#index - 1, true);
      return;
    }
    if (target.closest('[data-td-ss-next]')) {
      this.#go(this.#index + 1, true);
      return;
    }
    if (target.closest('[data-td-ss-toggle]')) {
      this.#paused = !this.#paused;
      this.#schedule();
    }
  };

  /** A swipe must not open the link under the finger */
  #onClickCapture = (/** @type {MouseEvent} */ event) => {
    if (!this.#swiped) return;
    this.#swiped = false;
    event.preventDefault();
    event.stopPropagation();
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target?.closest('.td-ss__controls')) return;
    let step = 0;
    if (event.key === 'ArrowRight') step = this.#rtl ? -1 : 1;
    else if (event.key === 'ArrowLeft') step = this.#rtl ? 1 : -1;
    else return;
    event.preventDefault();
    this.#go(this.#index + step, true);
    const dot = this.#dots[this.#index];
    if (target.closest('[data-td-ss-go]') && dot) dot.focus();
  };

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    if (event.pointerType === 'mouse') return;
    this.#touch = { x: event.clientX, y: event.clientY, id: event.pointerId, moved: false };
    this.#hold('touch');
  };

  /** @param {PointerEvent} event */
  #onPointerMove = (event) => {
    const touch = this.#touch;
    if (!touch || event.pointerId !== touch.id) return;
    if (Math.abs(event.clientX - touch.x) > 10) touch.moved = true;
  };

  /** @param {PointerEvent} event */
  #onPointerUp = (event) => {
    const touch = this.#touch;
    this.#touch = null;
    this.#release('touch');
    if (!touch || event.pointerId !== touch.id) return;
    const dx = event.clientX - touch.x;
    const dy = event.clientY - touch.y;
    if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.2) return;
    this.#swiped = true;
    window.setTimeout(() => (this.#swiped = false), 400);
    const forward = this.#rtl ? dx > 0 : dx < 0;
    this.#go(this.#index + (forward ? 1 : -1), true);
  };
}

// ---------------------------------------------------------------------------
// Product photos follow the chosen colour: a short fade when the photo changes
// ---------------------------------------------------------------------------

/** @param {Element | null | undefined} gallery */
function firstPhotoKey(gallery) {
  const img = gallery?.querySelector('slideshow-slide img, .product-media img');
  return img ? (img.getAttribute('src') || '').split('?')[0] : '';
}

/**
 * An option without a price yet (0) cannot be bought. Horizon turns the add-to-cart button back on
 * from availability alone after a variant change, so the theme turns it off again here, and marks
 * it so a click or a form submit can never go through while it shows "Price coming soon".
 * @param {Element | null} scope
 * @param {any} resource - the selected variant
 */
function guardUnpriced(scope, resource) {
  if (!scope) return;
  const unpriced = Boolean(resource) && Number(resource.price) === 0;
  for (const block of scope.querySelectorAll('[data-td-zero-guard]')) {
    const button = block.querySelector('[ref="addToCartButton"]');
    if (button instanceof HTMLButtonElement) {
      if (unpriced) button.disabled = true;
      button.toggleAttribute('data-td-unpriced', unpriced);
    }
    if (unpriced) block.querySelector('[ref="acceleratedCheckoutButtonContainer"]')?.setAttribute('hidden', 'true');
  }
}

document.addEventListener(
  'click',
  (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target?.closest('[data-td-unpriced]')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  },
  true
);
document.addEventListener(
  'submit',
  (event) => {
    const form = event.target instanceof HTMLFormElement ? event.target : null;
    if (!form?.querySelector('[data-td-unpriced]')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  },
  true
);

document.addEventListener(PRODUCT_SELECT, (/** @type {Event & {promise?: Promise<any>}} */ event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target || target.closest('product-card') || !event.promise) return;
  const scope = closestScope(target);
  const before = firstPhotoKey(scope?.querySelector('media-gallery'));
  event.promise
    .then((result) => {
      // After Horizon's own handlers have run for this selection
      window.setTimeout(() => guardUnpriced(scope, result?.detail?.resource), 0);
      rememberVariant(scope, result?.detail?.resource);
      window.requestAnimationFrame(() => {
        const gallery = scope?.querySelector('media-gallery');
        if (!gallery) return;
        if (!reducedMotion.matches && before && firstPhotoKey(gallery) !== before) {
          gallery.classList.add('td-gallery-swap');
          window.setTimeout(() => gallery.classList.remove('td-gallery-swap'), 700);
        }
        if (scope) initStrikes(scope);
      });
    })
    .catch(() => {});
});

// ---------------------------------------------------------------------------
// Product cards: pointing at a colour dot shows that colour's photo
// ---------------------------------------------------------------------------

/** @param {Element | null} card */
function cardPhoto(card) {
  return /** @type {HTMLImageElement | null} */ (card?.querySelector('.card-gallery img.product-media__image, .card-gallery img') || null);
}

/** @param {Element | null} card */
function restoreCardPhoto(card) {
  const img = cardPhoto(card);
  if (!img || img.dataset.tdSrc === undefined) return;
  if (img.dataset.tdSrcset) img.setAttribute('srcset', img.dataset.tdSrcset);
  else img.removeAttribute('srcset');
  img.setAttribute('src', img.dataset.tdSrc);
  card?.classList.remove('td-previewing');
}

document.addEventListener('pointerover', (event) => {
  if (event.pointerType !== 'mouse') return;
  const dot = event.target instanceof Element ? event.target.closest('.td-dot[data-td-preview]') : null;
  if (!(dot instanceof HTMLElement)) return;
  const card = dot.closest('product-card');
  const img = cardPhoto(card);
  if (!img) return;
  if (img.dataset.tdSrc === undefined) {
    img.dataset.tdSrc = img.getAttribute('src') || '';
    img.dataset.tdSrcset = img.getAttribute('srcset') || '';
  }
  // One 600 px photo is enough for a card under a mouse pointer (28 Oct 2026: the lists of sizes are gone)
  img.removeAttribute('srcset');
  img.setAttribute('src', dot.dataset.tdPreview || img.dataset.tdSrc);
  card?.classList.add('td-previewing');
});

document.addEventListener('pointerout', (event) => {
  const dots = event.target instanceof Element ? event.target.closest('.td-dots') : null;
  if (!dots) return;
  if (event.relatedTarget instanceof Node && dots.contains(event.relatedTarget)) return;
  restoreCardPhoto(dots.closest('product-card'));
});

// ---------------------------------------------------------------------------
// Compare-at price: the line through it draws in once the price is on screen
// ---------------------------------------------------------------------------

/** @type {IntersectionObserver | null} */
let strikeObserver = null;

/** @param {ParentNode} [root] */
function initStrikes(root = document) {
  if (reducedMotion.matches || !('IntersectionObserver' in window)) return;
  strikeObserver ||= new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-drawn');
        strikeObserver?.unobserve(entry.target);
      }
    },
    { threshold: 0.6 }
  );
  root.querySelectorAll('.td-strike:not(.td-strike--anim)').forEach((element) => {
    element.classList.add('td-strike--anim');
    strikeObserver?.observe(element);
  });
}

// ---------------------------------------------------------------------------
// Rows that scroll sideways (reviews, devices by category): arrows on larger screens, swipe on
// phones, and a soft edge on the side that has more to show
// ---------------------------------------------------------------------------

class TdRail {
  /**
   * @param {HTMLElement} root - holds the [data-td-rail-prev] and [data-td-rail-next] buttons
   * @param {AbortSignal} signal
   */
  constructor(root, signal) {
    this.root = root;
    this.nav = /** @type {HTMLElement | null} */ (root.querySelector('[data-td-rail-nav]'));
    this.prev = /** @type {HTMLButtonElement | null} */ (root.querySelector('[data-td-rail-prev]'));
    this.next = /** @type {HTMLButtonElement | null} */ (root.querySelector('[data-td-rail-next]'));
    /** @type {HTMLElement | null} */
    this.track = null;
    this.frame = 0;
    this.observer = 'ResizeObserver' in window ? new ResizeObserver(() => this.update()) : null;
    this.prev?.addEventListener('click', () => this.step(-1), { signal });
    this.next?.addEventListener('click', () => this.step(1), { signal });
    signal.addEventListener('abort', () => {
      this.observer?.disconnect();
      this.track?.removeEventListener('scroll', this.onScroll);
      if (this.frame) cancelAnimationFrame(this.frame);
    });
  }

  /** @param {Element | null | undefined} track - the row to control from now on */
  use(track) {
    const next = track instanceof HTMLElement ? track : null;
    if (this.track !== next) {
      if (this.track) {
        this.track.removeEventListener('scroll', this.onScroll);
        this.observer?.unobserve(this.track);
      }
      this.track = next;
      if (next) {
        next.addEventListener('scroll', this.onScroll, { passive: true });
        this.observer?.observe(next);
      }
    }
    this.update();
  }

  onScroll = () => {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = 0;
      this.update();
    });
  };

  update() {
    const track = this.track;
    if (!track) {
      if (this.nav) this.nav.hidden = true;
      return;
    }
    const max = track.scrollWidth - track.clientWidth;
    const position = Math.abs(track.scrollLeft);
    const overflow = max > 4;
    const atStart = position <= 2;
    const atEnd = position >= max - 2;
    if (this.nav) this.nav.hidden = !overflow;
    if (this.prev) this.prev.disabled = !overflow || atStart;
    if (this.next) this.next.disabled = !overflow || atEnd;
    track.classList.toggle('td-fade-start', overflow && !atStart);
    track.classList.toggle('td-fade-end', overflow && !atEnd);
  }

  /** @param {number} direction - 1 forward, -1 back (in reading order) */
  step(direction) {
    const track = this.track;
    if (!track) return;
    const item = track.querySelector('.td-rail__item');
    const gap = parseFloat(getComputedStyle(track).columnGap) || 16;
    const size = item ? item.getBoundingClientRect().width + gap : track.clientWidth * 0.8;
    const perView = Math.max(1, Math.floor((track.clientWidth + gap) / size));
    const rtl = getComputedStyle(track).direction === 'rtl';
    track.scrollBy({ left: size * perView * direction * (rtl ? -1 : 1), behavior: reducedMotion.matches ? 'auto' : 'smooth' });
  }
}

// ---------------------------------------------------------------------------
// What we check: a row of check cards near the top of the product page (arrows on larger
// screens); each card opens the inspection panel on its check (see showCheck)
// ---------------------------------------------------------------------------

class TdCheckCards extends HTMLElement {
  #abort = new AbortController();

  connectedCallback() {
    this.#abort = new AbortController();
    new TdRail(this, this.#abort.signal).use(this.querySelector('[data-td-rail-track]'));
  }

  disconnectedCallback() {
    this.#abort.abort();
  }
}

// ---------------------------------------------------------------------------
// <td-rail-box>: a row with its own arrows, such as "Shop by series" on collection pages. The
// tile of the page being viewed (aria-current) is brought into the row's view on arrival.
// ---------------------------------------------------------------------------

class TdRailBox extends HTMLElement {
  #abort = new AbortController();

  connectedCallback() {
    this.#abort = new AbortController();
    const track = this.querySelector('[data-td-rail-track]');
    new TdRail(this, this.#abort.signal).use(track);
    const current = this.querySelector('[aria-current="page"]');
    if (!(track instanceof HTMLElement) || !(current instanceof HTMLElement)) return;
    requestAnimationFrame(() => {
      const row = track.getBoundingClientRect();
      const tile = current.getBoundingClientRect();
      if (tile.left < row.left || tile.right > row.right) {
        track.scrollLeft += tile.left + tile.width / 2 - (row.left + row.width / 2);
      }
    });
  }

  disconnectedCallback() {
    this.#abort.abort();
  }
}

// ---------------------------------------------------------------------------
// <td-turns>: words that take turns in one place, such as the model names after "Search for" in
// the phone search field. Each word fades in from just below. Still for shoppers who ask for less
// motion, and paused while the page is hidden.
// ---------------------------------------------------------------------------

class TdTurns extends HTMLElement {
  #timer = 0;
  #index = 0;

  connectedCallback() {
    if (reducedMotion.matches) return;
    if (this.#words().length < 2) return;
    document.addEventListener('visibilitychange', this.#onVisibility);
    // Not while the page is still loading: each turn measures, which forces style work in the middle of it
    const begin = () => {
      if (this.isConnected) this.#start();
    };
    if (document.readyState === 'complete') begin();
    else window.addEventListener('load', begin, { once: true });
  }

  disconnectedCallback() {
    document.removeEventListener('visibilitychange', this.#onVisibility);
    this.#stop();
  }

  #words() {
    return /** @type {HTMLElement[]} */ ([...this.children].filter((child) => child instanceof HTMLElement));
  }

  #onVisibility = () => {
    if (document.hidden) this.#stop();
    else this.#start();
  };

  #start() {
    this.#stop();
    const every = Math.max(1800, Number(this.dataset.interval) || 2600);
    this.#timer = window.setInterval(this.#next, every);
  }

  #stop() {
    if (this.#timer) window.clearInterval(this.#timer);
    this.#timer = 0;
  }

  #next = () => {
    // Nothing to do while the field is hidden (larger screens)
    if (!this.offsetParent) return;
    const words = this.#words();
    const leaving = words[this.#index];
    this.#index = (this.#index + 1) % words.length;
    const coming = words[this.#index];
    leaving?.classList.remove('is-on');
    leaving?.classList.add('is-leaving');
    // The next name comes in once the last one has gone, so the two never sit on top of each other
    window.setTimeout(() => {
      leaving?.classList.remove('is-leaving');
      coming?.classList.add('is-on');
    }, 200);
  };
}

// ---------------------------------------------------------------------------
// Hologram stickers (<td-holo>): the light on the foil (--td-holo-p, 0 to 1) follows the sticker's
// place on the screen and how far the page has scrolled, so it keeps moving on the product photos
// that stay in place on a computer, and swings back and forth instead of jumping. Horizon scrolls
// inside .page-wrapper, so scrolls are caught at the document. On a computer the pointer over the
// sticker's area moves the light too (data-td-holo-area). Only stickers on screen are updated, at
// most once a frame; with reduced motion the light stays still. Lighter on phones (28 Oct 2026): the
// light moves in 96 fine steps, so a sticker is redrawn only when its step changes, and a weak phone
// (a touch screen with 4 processor cores or 4 GB of memory or less) redraws at most 30 times a second.
// ---------------------------------------------------------------------------

/** @type {Set<HTMLElement>} */
const holoOnScreen = new Set();
let holoFrame = 0;
let holoScrolled = 0;
let holoLast = 0;
const HOLO_SWEEP_PX = 900;
const HOLO_STEPS = 96;
const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
const holoWeak =
  window.matchMedia('(pointer: coarse)').matches &&
  ((navigator.hardwareConcurrency || 8) <= 4 || (/** @type {any} */ (navigator).deviceMemory || 8) <= 4);

/** @param {number} [now] */
function holoUpdate(now = performance.now()) {
  holoFrame = 0;
  if (holoWeak && now - holoLast < 32) {
    holoSchedule();
    return;
  }
  holoLast = now;
  const height = window.innerHeight || 800;
  for (const holo of holoOnScreen) {
    if (holo.dataset.tdHoloPointer) continue;
    let light = 0.5;
    if (!reducedMotion.matches) {
      const rect = holo.getBoundingClientRect();
      const place = 1 - (rect.top + rect.height / 2) / height;
      const wave = (((place + holoScrolled / HOLO_SWEEP_PX) % 2) + 2) % 2;
      light = wave <= 1 ? wave : 2 - wave;
    }
    const value = (Math.round(light * HOLO_STEPS) / HOLO_STEPS).toFixed(3);
    if (holo.style.getPropertyValue('--td-holo-p') !== value) holo.style.setProperty('--td-holo-p', value);
  }
}

function holoSchedule() {
  // Not while the page is still loading: measuring forces style work in the middle of it, and the foil starts at its resting light
  if (document.readyState !== 'complete') return;
  if (!holoFrame) holoFrame = requestAnimationFrame(holoUpdate);
}

window.addEventListener('load', holoSchedule, { once: true });

/** @param {Event} event */
function holoScroll(event) {
  const target = event.target;
  if (target === document || target === document.documentElement || target === document.body) {
    holoScrolled = window.scrollY;
  } else if (target instanceof HTMLElement && target.classList.contains('page-wrapper')) {
    holoScrolled = target.scrollTop;
  }
  holoSchedule();
}

const holoObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        for (const entry of entries) {
          const holo = /** @type {HTMLElement} */ (entry.target);
          if (entry.isIntersecting) holoOnScreen.add(holo);
          else holoOnScreen.delete(holo);
        }
        holoSchedule();
      })
    : null;

document.addEventListener('scroll', holoScroll, { capture: true, passive: true });
window.addEventListener('resize', holoSchedule, { passive: true });

class TdHolo extends HTMLElement {
  #abort = new AbortController();

  connectedCallback() {
    this.#abort = new AbortController();
    if (holoObserver) holoObserver.observe(this);
    else holoOnScreen.add(this);
    holoSchedule();
    if (!finePointer.matches || reducedMotion.matches) return;
    const selector = this.dataset.tdHoloArea;
    const area = (selector && this.closest(selector)) || this;
    const { signal } = this.#abort;
    area.addEventListener(
      'pointermove',
      (event) => {
        const rect = this.getBoundingClientRect();
        if (!rect.width) return;
        const x = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
        const y = Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height));
        this.dataset.tdHoloPointer = '1';
        this.style.setProperty('--td-holo-x', `${(x * 100).toFixed(1)}%`);
        this.style.setProperty('--td-holo-y', `${(y * 100).toFixed(1)}%`);
        this.style.setProperty('--td-holo-p', (0.15 + x * 0.7).toFixed(3));
      },
      { signal, passive: true }
    );
    area.addEventListener(
      'pointerleave',
      () => {
        delete this.dataset.tdHoloPointer;
        this.style.removeProperty('--td-holo-x');
        this.style.removeProperty('--td-holo-y');
        holoSchedule();
      },
      { signal, passive: true }
    );
  }

  disconnectedCallback() {
    this.#abort.abort();
    holoObserver?.unobserve(this);
    holoOnScreen.delete(this);
  }
}

// ---------------------------------------------------------------------------
// Customer reviews: a card opens the full review (photos, stars, text, device) in the panel
// ---------------------------------------------------------------------------

class TdReviews extends HTMLElement {
  #abort = new AbortController();
  /** @type {TdRail | null} */
  #rail = null;
  #filter = '';

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.#rail = new TdRail(this, signal);
    this.#rail.use(this.querySelector('[data-td-rail-track]'));
    this.addEventListener('click', this.#onClick, { signal });
    this.#buildFilters();
    this.#showPending();
  }

  disconnectedCallback() {
    this.#abort.abort();
  }

  /** @returns {HTMLElement[]} the published review cards (not the editor's samples or a review waiting for approval) */
  #items() {
    return /** @type {HTMLElement[]} */ (
      Array.from(
        this.querySelectorAll('.td-rv__list:not(.td-rv__list--sample) > .td-rv__item:not(.td-rv__item--pending)')
      )
    );
  }

  /**
   * The review this shopper sent from the form page (td-review-form keeps it in localStorage) shows to them
   * alone, marked as waiting for approval, until it is published or td_review_pending_days pass.
   */
  #showPending() {
    const model = this.dataset.model;
    if (!model) return;
    /** @type {Array<{model: string, author: string, rating: number, body: string, ts: number}>} */
    let entries = [];
    try {
      entries = JSON.parse(localStorage.getItem('td-rv-pending') || '[]');
    } catch {
      return;
    }
    if (!Array.isArray(entries) || !entries.length) return;
    const days = Number(this.dataset.pendingDays) || 14;
    const now = Date.now();
    const norm = (/** @type {string} */ text) => String(text || '').replace(/\s+/g, ' ').trim().slice(0, 60);
    const published = new Set(this.#items().map((item) => norm(item.dataset.tdText || '')));
    const keep = entries.filter(
      (entry) => entry && typeof entry.ts === 'number' && now - entry.ts < days * 864e5 && !published.has(norm(entry.body))
    );
    if (keep.length !== entries.length) {
      try {
        localStorage.setItem('td-rv-pending', JSON.stringify(keep));
      } catch {
        // Storage can be full or blocked; the card simply shows again next time
      }
    }
    const list = this.querySelector('.td-rv__list:not(.td-rv__list--sample)');
    const mine = keep.filter((entry) => entry.model === model);
    if (!mine.length || !(list instanceof HTMLElement)) return;
    for (const entry of mine.reverse()) list.prepend(this.#pendingCard(entry));
    const rail = list.closest('.td-rail');
    if (rail instanceof HTMLElement) rail.hidden = false;
    this.#rail?.update();
  }

  /** @param {{author: string, rating: number, body: string}} entry */
  #pendingCard(entry) {
    const make = (/** @type {string} */ tag, /** @type {string} */ className, /** @type {string} */ text = '') => {
      const node = document.createElement(tag);
      node.className = className;
      if (text) node.textContent = text;
      return node;
    };
    const rating = Math.min(5, Math.max(1, Math.round(Number(entry.rating) || 0)));
    const item = make('li', 'td-rail__item td-rv__item td-rv__item--pending');
    const card = make('div', 'td-rv__card td-rv__card--words td-rv__card--pending');
    const media = make('span', 'td-rv__media');
    const over = make('span', 'td-rv__over');
    const stars = make('span', 'td-rv__stars-text', '★'.repeat(rating) + '☆'.repeat(5 - rating));
    stars.setAttribute('role', 'img');
    stars.setAttribute('aria-label', (this.dataset.tStars || '').replace('[r]', String(rating)));
    const rate = make('span', 'td-rv__rate');
    rate.append(stars);
    over.append(make('span', 'td-rv__excerpt', String(entry.body || '').slice(0, 420)), rate);
    media.append(make('span', 'td-rv__name', String(entry.author || '')), over);
    const product = make('span', 'td-rv__product');
    const note = make('span', 'td-rv__product-text');
    note.append(make('span', 'td-rv__product-meta', this.dataset.tPendingNote || ''));
    product.append(note);
    card.append(make('span', 'td-rv__pending', this.dataset.tPending || ''), media, product);
    item.append(card);
    return item;
  }

  /** Chips under the heading: all, each condition the reviews name, and reviews with photos */
  #buildFilters() {
    const box = this.querySelector('[data-td-rv-filters]');
    const items = this.#items();
    if (!(box instanceof HTMLElement) || items.length < 3) return;
    /** @type {Map<string, number>} */
    const grades = new Map();
    let photos = 0;
    for (const item of items) {
      const grade = item.dataset.grade;
      if (grade) grades.set(grade, (grades.get(grade) || 0) + 1);
      if (item.hasAttribute('data-photos')) photos += 1;
    }
    const chip = (/** @type {string} */ value, /** @type {string} */ label) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'td-rv__chip';
      button.dataset.tdRvFilter = value;
      button.setAttribute('aria-pressed', String(value === ''));
      button.textContent = label;
      return button;
    };
    const chips = [chip('', (box.dataset.tAll || '').replace('[n]', String(items.length)))];
    for (const pair of (box.dataset.grades || '').split('|')) {
      const split = pair.indexOf(':');
      if (split < 1) continue;
      const index = pair.slice(0, split);
      const count = grades.get(index);
      if (count) chips.push(chip(`grade:${index}`, `${pair.slice(split + 1)} (${count})`));
    }
    if (photos > 0 && photos < items.length) chips.push(chip('photos', `${box.dataset.tPhotos || ''} (${photos})`));
    if (chips.length < 2) return;
    const status = document.createElement('p');
    status.className = 'td-rv__status';
    status.setAttribute('aria-live', 'polite');
    status.dataset.tdRvStatus = '';
    box.replaceChildren(...chips, status);
    box.hidden = false;
  }

  /** @param {string} value - '', stars:5, grade:2 or photos; choosing the active one shows all */
  #applyFilter(value) {
    this.#filter = this.#filter === value ? '' : value;
    const items = this.#items();
    let shown = 0;
    for (const item of items) {
      let match = true;
      if (this.#filter.startsWith('stars:')) match = item.dataset.stars === this.#filter.slice(6);
      else if (this.#filter.startsWith('grade:')) match = item.dataset.grade === this.#filter.slice(6);
      else if (this.#filter === 'photos') match = item.hasAttribute('data-photos');
      item.hidden = !match;
      if (match) shown += 1;
    }
    for (const item of this.querySelectorAll('.td-rv__item--pending')) {
      if (item instanceof HTMLElement) item.hidden = this.#filter !== '';
    }
    for (const button of this.querySelectorAll('[data-td-rv-filter]')) {
      if (button instanceof HTMLElement) button.setAttribute('aria-pressed', String((button.dataset.tdRvFilter || '') === this.#filter));
    }
    for (const bar of this.querySelectorAll('[data-td-rv-stars]')) {
      if (bar instanceof HTMLElement) bar.setAttribute('aria-pressed', String(`stars:${bar.dataset.tdRvStars}` === this.#filter));
    }
    const box = this.querySelector('[data-td-rv-filters]');
    const status = this.querySelector('[data-td-rv-status]');
    if (status && box instanceof HTMLElement) {
      status.textContent = this.#filter
        ? (box.dataset.tShowing || '').replace('[s]', String(shown)).replace('[t]', String(items.length))
        : '';
    }
    const track = this.querySelector('[data-td-rail-track]');
    if (track instanceof HTMLElement) track.scrollTo({ left: 0 });
    this.#rail?.update();
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const bar = target.closest('[data-td-rv-stars]');
    if (bar instanceof HTMLElement) {
      this.#applyFilter(`stars:${bar.dataset.tdRvStars}`);
      return;
    }
    const filter = target.closest('[data-td-rv-filter]');
    if (filter instanceof HTMLElement) {
      this.#applyFilter(filter.dataset.tdRvFilter || '');
      return;
    }
    const card = target.closest('[data-td-review]');
    if (card instanceof HTMLElement) {
      this.#open(card);
      return;
    }
    const more = target.closest('[data-td-review-more]');
    if (more instanceof HTMLButtonElement) {
      this.#toggleMore(more);
      return;
    }
    const thumb = target.closest('.td-rvd__thumb');
    if (thumb instanceof HTMLButtonElement) this.#showPhoto(thumb);
  };

  /** @param {HTMLElement} card */
  async #open(card) {
    const id = card.dataset.tdReview || '';
    const template = this.querySelector(`template[data-td-review-tpl="${CSS.escape(id)}"]`);
    const host = document.getElementById(this.dataset.dialog || '');
    const slot = host?.querySelector('[data-td-review-slot]');
    if (!(template instanceof HTMLTemplateElement) || !host || !(slot instanceof HTMLElement)) return;

    slot.replaceChildren(template.content.cloneNode(true));
    slot.scrollTop = 0;
    const title = host.querySelector('[data-td-review-title]');
    if (title instanceof HTMLElement) {
      const productTitle = slot.querySelector('.td-rvd')?.getAttribute('data-title') || '';
      title.textContent = productTitle || title.dataset.default || '';
    }

    await openPanel(host.id);
    requestAnimationFrame(() => this.#measure(slot));
  }

  /** "Read more" shows only when the text is longer than the five lines in view */
  #measure(/** @type {HTMLElement} */ slot) {
    const text = slot.querySelector('[data-td-review-text]');
    const more = slot.querySelector('[data-td-review-more]');
    if (!(text instanceof HTMLElement) || !(more instanceof HTMLElement)) return;
    more.hidden = text.scrollHeight <= text.clientHeight + 2;
  }

  /** @param {HTMLButtonElement} button */
  #toggleMore(button) {
    const text = document.getElementById(button.getAttribute('aria-controls') || '');
    const open = button.getAttribute('aria-expanded') !== 'true';
    button.setAttribute('aria-expanded', String(open));
    text?.classList.toggle('is-open', open);
    const label = open ? button.dataset.labelLess : button.dataset.labelMore;
    if (label) button.textContent = label;
  }

  /** @param {HTMLButtonElement} thumb */
  #showPhoto(thumb) {
    const view = thumb.closest('.td-rvd');
    const image = view?.querySelector('.td-rvd__img');
    if (!view || !(image instanceof HTMLImageElement)) return;
    if (thumb.dataset.srcset) image.srcset = thumb.dataset.srcset;
    if (thumb.dataset.src) image.src = thumb.dataset.src;
    if (thumb.dataset.alt) image.alt = thumb.dataset.alt;
    for (const other of view.querySelectorAll('.td-rvd__thumb')) {
      other.setAttribute('aria-pressed', String(other === thumb));
    }
  }
}

// ---------------------------------------------------------------------------
// Devices by category: tiles swap the row of devices in place (ARIA tabs)
// ---------------------------------------------------------------------------

class TdProductTabs extends HTMLElement {
  #abort = new AbortController();
  /** @type {TdRail | null} */
  #rail = null;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.#rail = new TdRail(this, signal);
    const selected = this.querySelector('[role="tab"][aria-selected="true"]');
    this.#rail.use(this.#panelOf(selected)?.querySelector('[data-td-rail-track]'));
    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('keydown', this.#onKeydown, { signal });
  }

  disconnectedCallback() {
    this.#abort.abort();
  }

  get #tabs() {
    return /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[role="tab"]')));
  }

  /** @param {Element | null} tab */
  #panelOf(tab) {
    return tab ? document.getElementById(tab.getAttribute('aria-controls') || '') : null;
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const tab = event.target instanceof Element ? event.target.closest('[role="tab"]') : null;
    if (tab instanceof HTMLElement && this.contains(tab)) this.#select(tab);
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    const tab = event.target instanceof Element ? event.target.closest('[role="tab"]') : null;
    if (!(tab instanceof HTMLElement)) return;
    const tabs = this.#tabs;
    const index = tabs.indexOf(tab);
    const rtl = getComputedStyle(tab).direction === 'rtl';
    let next = -1;
    if (event.key === 'ArrowRight') next = rtl ? index - 1 : index + 1;
    else if (event.key === 'ArrowLeft') next = rtl ? index + 1 : index - 1;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabs.length - 1;
    else return;
    event.preventDefault();
    const target = tabs[(next + tabs.length) % tabs.length];
    if (!target) return;
    this.#select(target);
    target.focus();
  };

  /** @param {HTMLElement} tab */
  #select(tab) {
    if (tab.getAttribute('aria-selected') === 'true') return;
    for (const other of this.#tabs) {
      const on = other === tab;
      other.setAttribute('aria-selected', String(on));
      other.tabIndex = on ? 0 : -1;
      const panel = this.#panelOf(other);
      if (!panel) continue;
      panel.hidden = !on;
      if (on) {
        panel.classList.remove('is-entering');
        void panel.offsetWidth;
        panel.classList.add('is-entering');
      }
    }
    const all = this.querySelector('[data-td-pt-all]');
    if (all instanceof HTMLAnchorElement && tab.dataset.url) all.href = tab.dataset.url;
    // Keep the chosen tile in view when the tiles scroll sideways on phones
    const row = tab.parentElement;
    if (row && row.scrollWidth > row.clientWidth) {
      tab.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    }
    this.#rail?.use(this.#panelOf(tab)?.querySelector('[data-td-rail-track]'));
  }
}

// ---------------------------------------------------------------------------
// Device sizes (snippets/td-size-data): Apple's published sizes per model, and product handles
// ---------------------------------------------------------------------------

/**
 * @typedef {{id: string, n: string, a?: string, k: string, h: number, w: number, d?: number, g?: number, s?: number}} TdSizeModel
 * @typedef {{m: TdSizeModel[], h: Record<string, string>}} TdSizes
 */

/** @param {TdSizeModel} model - the model's name in the page language */
function modelName(model) {
  return (document.documentElement.lang.startsWith('ar') && model.a) || model.n;
}

/** @type {Promise<TdSizes | null> | null} */
let sizesLoad = null;

/**
 * Apple's sizes, from assets/td-sizes.js (import map name @td/sizes): loaded once, when a page needs them,
 * and kept by the browser for the next pages.
 * @returns {Promise<TdSizes | null>}
 */
function loadSizes() {
  sizesLoad ||= import('@td/sizes')
    .then((module) => (module.default && Array.isArray(module.default.m) ? /** @type {TdSizes} */ (module.default) : null))
    .catch(() => null);
  return sizesLoad;
}

/** A SIM version's handle without its ending: iphone-16-pro-max-esim-only gives iphone-16-pro-max */
const SIM_ENDINGS = ['-nano-sim-esim', '-esim-only', '-dual-nano-sim', '-nano-sim'];

/** @param {string} handle */
function simBase(handle) {
  const ending = SIM_ENDINGS.find((end) => handle.endsWith(end));
  return ending ? handle.slice(0, -ending.length) : handle;
}

/**
 * @param {TdSizes} sizes
 * @param {string} handleOrId - a product handle (a SIM version too) or a model id
 */
function sizeModel(sizes, handleOrId) {
  const id = sizes.h[handleOrId] || sizes.h[simBase(handleOrId)] || handleOrId;
  return sizes.m.find((model) => model.id === id) || null;
}

/**
 * @param {number} value
 * @param {number} [decimals] - at most this many decimals, trailing zeros dropped (Apple's 8.25 stays 8.25)
 */
function trimNumber(value, decimals = 2) {
  const factor = 10 ** decimals;
  return String(Math.round(value * factor) / factor);
}

/**
 * @param {string} template - text with [v] for the value
 * @param {number | string} value
 */
function fill(template, value) {
  return (template || '').replace('[v]', String(value));
}

const MY_DEVICE_KEY = 'td-my-device-';

/**
 * Opens the size comparison panel with a model chosen (from the model comparison).
 * @param {string} handle
 */
function openSizeFor(handle) {
  const size = document.querySelector('td-size');
  if (!(size instanceof TdSize)) return;
  size.choose(handle);
  const host = size.closest('dialog-component');
  if (host?.id) openPanel(host.id);
}

// ---------------------------------------------------------------------------
// Size comparison: this device drawn over the shopper's own phone or tablet, to scale
// ---------------------------------------------------------------------------

class TdSize extends HTMLElement {
  /** @type {TdSizes | null} */
  #sizes = null;
  /** @type {TdSizeModel | null} */
  #current = null;
  /** @type {HTMLSelectElement | null} */
  #select = null;
  /** @type {Promise<void> | null} */
  #ready = null;

  connectedCallback() {
    this.#ready = loadSizes().then((sizes) => this.#setup(sizes));
  }

  /** @param {TdSizes | null} sizes */
  #setup(sizes) {
    this.#sizes = sizes;
    this.#select = this.querySelector('[data-td-size-select]');
    if (!this.#sizes || !this.#select) return;
    this.#current = sizeModel(this.#sizes, this.dataset.current || '');
    const current = this.#current;
    if (!current) return;

    const kind = current.k;
    const options = this.#sizes.m.filter((model) => model.k === kind && model.id !== current.id);
    this.#select.replaceChildren(...options.map((model) => new Option(modelName(model), model.id)));

    let stored = '';
    try {
      stored = localStorage.getItem(MY_DEVICE_KEY + kind) || '';
    } catch (error) {
      stored = '';
    }
    const fallback = kind === 'p' ? (current.id === 'iphone-13' ? 'iphone-11' : 'iphone-13') : 'ipad-9th-generation';
    const wanted = [stored, fallback].find((id) => id && options.some((model) => model.id === id));
    this.#select.value = wanted || options[0]?.id || '';

    this.#select.addEventListener('change', () => {
      try {
        localStorage.setItem(MY_DEVICE_KEY + kind, this.#select?.value || '');
      } catch (error) {
        // private mode: the choice lasts for this page only
      }
      this.render();
    });
    this.render();
  }

  /** @param {string} handleOrId */
  choose(handleOrId) {
    (this.#ready || Promise.resolve()).then(() => {
      if (!this.#sizes || !this.#select) return;
      const model = sizeModel(this.#sizes, handleOrId);
      if (!model || ![...this.#select.options].some((option) => option.value === model.id)) return;
      this.#select.value = model.id;
      this.render();
    });
  }

  render() {
    const sizes = this.#sizes;
    const a = this.#current;
    const b = sizes && this.#select ? sizeModel(sizes, this.#select.value) : null;
    if (!a || !b) return;

    // Drawing: both outlines standing on the same line, centred, in millimetres
    const svg = this.querySelector('[data-td-size-svg]');
    const pad = 3;
    const width = Math.max(a.w, b.w) + pad * 2;
    const height = Math.max(a.h, b.h) + pad * 2;
    svg?.setAttribute('viewBox', `0 0 ${width} ${height}`);
    const place = (/** @type {Element | null} */ rect, /** @type {TdSizeModel} */ model) => {
      if (!(rect instanceof SVGRectElement)) return;
      const radius = model.w * (model.k === 'p' ? 0.15 : 0.07);
      const geometry = {
        x: (width - model.w) / 2,
        y: height - pad - model.h,
        width: model.w,
        height: model.h,
        rx: radius,
      };
      for (const [name, value] of Object.entries(geometry)) {
        rect.setAttribute(name, String(value));
        rect.style.setProperty(name, `${value}px`);
      }
    };
    place(this.querySelector('[data-td-size-this]'), a);
    place(this.querySelector('[data-td-size-other]'), b);
    svg?.setAttribute(
      'aria-label',
      (this.dataset.tDrawing || '').replace('[a]', this.dataset.currentName || modelName(a)).replace('[b]', modelName(b))
    );

    const otherName = this.querySelector('[data-td-size-other-name]');
    if (otherName) otherName.textContent = modelName(b);
    const head = this.querySelector('[data-td-size-other-head]');
    if (head) head.textContent = modelName(b);
    const versus = this.querySelector('[data-td-size-versus]');
    if (versus) versus.textContent = (this.dataset.tVersus || '').replace('[m]', modelName(b));

    // Differences: taller or shorter, wider or narrower, heavier or lighter
    const deltas = [];
    const dh = a.h - b.h;
    const dw = a.w - b.w;
    const dg = (a.g || 0) - (b.g || 0);
    if (Math.abs(dh) >= 0.5) deltas.push(fill(dh > 0 ? this.dataset.tTaller || '' : this.dataset.tShorter || '', trimNumber(Math.abs(dh), 1)));
    if (Math.abs(dw) >= 0.5) deltas.push(fill(dw > 0 ? this.dataset.tWider || '' : this.dataset.tNarrower || '', trimNumber(Math.abs(dw), 1)));
    if (a.g && b.g && Math.abs(dg) >= 1) {
      deltas.push(fill(dg > 0 ? this.dataset.tHeavier || '' : this.dataset.tLighter || '', Math.round(Math.abs(dg))));
    }
    if (deltas.length === 0) deltas.push(this.dataset.tSame || '');
    const list = this.querySelector('[data-td-size-deltas]');
    list?.replaceChildren(
      ...deltas.map((text) => {
        const item = document.createElement('li');
        item.textContent = text;
        return item;
      })
    );

    // Table: this device and the other one, row by row
    /** @type {Record<string, (model: TdSizeModel) => string>} */
    const cells = {
      height: (model) => fill(this.dataset.tMm || '', trimNumber(model.h)),
      width: (model) => fill(this.dataset.tMm || '', trimNumber(model.w)),
      depth: (model) => (model.d ? fill(this.dataset.tMm || '', trimNumber(model.d)) : ''),
      weight: (model) => (model.g ? fill(this.dataset.tG || '', Math.round(model.g)) : ''),
      display: (model) => (model.s ? fill(this.dataset.tInch || '', trimNumber(model.s)) : ''),
    };
    for (const row of this.querySelectorAll('[data-td-size-row]')) {
      if (!(row instanceof HTMLElement)) continue;
      const make = cells[row.dataset.tdSizeRow || ''];
      if (!make) continue;
      const left = row.querySelector('[data-td-size-a]');
      const right = row.querySelector('[data-td-size-b]');
      const valueA = make(a);
      const valueB = make(b);
      if (left) left.textContent = valueA;
      if (right) right.textContent = valueB;
      row.hidden = !valueA && !valueB;
    }
  }
}

// ---------------------------------------------------------------------------
// Model comparison: "differences only", another model loaded in place, size and weight rows
// ---------------------------------------------------------------------------

/**
 * Formats an amount in the smallest currency unit with the shop's money format.
 * @param {number} cents
 * @param {string} format - for example "Dhs. {{amount}}"
 * @param {boolean} trim - drop .00
 */
function formatMoney(cents, format, trim) {
  const value = cents / 100;
  const number = (/** @type {number} */ decimals, /** @type {string} */ thousands, /** @type {string} */ point) => {
    const [whole = '0', fraction = ''] = value.toFixed(decimals).split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return fraction && !(trim && /^0+$/.test(fraction)) ? `${grouped}${point}${fraction}` : grouped;
  };
  return (format || '{{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (_match, key) => {
    switch (key) {
      case 'amount_no_decimals':
        return number(0, ',', '.');
      case 'amount_with_comma_separator':
        return number(2, '.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return number(0, '.', ',');
      case 'amount_with_apostrophe_separator':
        return number(2, "'", '.');
      default:
        return number(2, ',', '.');
    }
  });
}

class TdCompare extends HTMLElement {
  #abort = new AbortController();
  /** @type {TdSizes | null} */
  #sizes = null;
  #request = 0;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    const select = this.querySelector('[data-td-cmp-select]');
    const diff = this.querySelector('[data-td-cmp-diff]');
    diff?.addEventListener('change', () => this.classList.toggle('is-diff', diff instanceof HTMLInputElement && diff.checked), { signal });
    if (select instanceof HTMLSelectElement) {
      select.addEventListener('change', () => this.#load(select.value), { signal });
      // Size and weight rows fill in once the sizes have loaded
      if (this.querySelector('[data-td-cmp-sized], [data-td-size-compare]')) {
        loadSizes().then((sizes) => {
          if (signal.aborted) return;
          this.#sizes = sizes;
          this.#fillSizes(select.value);
        });
      }
    }
  }

  disconnectedCallback() {
    this.#abort.abort();
  }

  /** @param {string} handle - the other model */
  #fillSizes(handle) {
    const sizes = this.#sizes;
    const a = sizes ? sizeModel(sizes, this.dataset.this || '') : null;
    const b = sizes ? sizeModel(sizes, handle) : null;
    for (const row of this.querySelectorAll('[data-td-cmp-sized]')) {
      if (!(row instanceof HTMLElement)) continue;
      const weight = row.dataset.tdCmpRow === 'weight';
      const text = (/** @type {TdSizeModel | null} */ model) => {
        if (!model) return '';
        if (weight) return model.g ? fill(this.dataset.tG || '', Math.round(model.g)) : '';
        return fill(this.dataset.tMm || '', `${trimNumber(model.h)} × ${trimNumber(model.w)}`);
      };
      const valueA = text(a);
      const valueB = text(b);
      const left = row.querySelector('[data-td-cmp-a]');
      const right = row.querySelector('[data-td-cmp-b]');
      if (left) left.textContent = valueA;
      if (right) right.textContent = valueB;
      row.toggleAttribute('data-same', valueA === valueB);
      row.hidden = !valueA || !valueB;
    }
    const button = this.querySelector('[data-td-size-compare]');
    if (button instanceof HTMLElement) {
      button.dataset.tdSizeCompare = handle;
      button.hidden = !(a && b && document.querySelector('td-size'));
    }
  }

  /** @param {string} handle */
  async #load(handle) {
    const ticket = ++this.#request;
    const error = this.querySelector('[data-td-cmp-error]');
    if (error) error.textContent = '';
    this.setAttribute('aria-busy', 'true');
    try {
      const root = (this.dataset.root || '/').replace(/\/$/, '');
      const response = await fetch(`${root}/products/${encodeURIComponent(handle)}?section_id=td-compare-data`, {
        signal: this.#abort.signal,
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const script = doc.querySelector('script[data-td-compare-data]');
      const data = JSON.parse(script?.textContent || 'null');
      if (!data || ticket !== this.#request) return;
      this.#show(data);
      this.#fillSizes(handle);
    } catch (problem) {
      if (/** @type {any} */ (problem)?.name === 'AbortError') return;
      if (error) error.textContent = this.dataset.tError || '';
    } finally {
      if (ticket === this.#request) this.removeAttribute('aria-busy');
    }
  }

  /**
   * @param {{url: string, title: string, image: string, price: number, priceText: string, available?: boolean, specs: Record<string, string>}} data
   */
  #show(data) {
    for (const link of this.querySelectorAll('[data-td-cmp-media], [data-td-cmp-name]')) {
      if (link instanceof HTMLAnchorElement) link.href = data.url;
    }
    const name = this.querySelector('[data-td-cmp-name]');
    if (name) name.textContent = data.title;

    const media = this.querySelector('[data-td-cmp-media]');
    if (media) {
      if (data.image) {
        let image = media.querySelector('img');
        if (!image) {
          image = document.createElement('img');
          image.className = 'td-cmp__img';
          image.loading = 'lazy';
          image.alt = '';
          image.width = 360;
          image.height = 360;
          media.append(image);
        }
        image.removeAttribute('srcset');
        image.src = data.image;
      } else {
        media.replaceChildren();
      }
    }

    const price = this.querySelector('[data-td-cmp-price]');
    if (price) {
      // A model with nothing to buy reads "Sold out": this line means a price a shopper can buy at
      const soldOut = data.available === false && this.dataset.tSold;
      price.textContent =
        data.price > 0 ? `${this.dataset.tFrom || ''} ${data.priceText}` : soldOut || this.dataset.tSoon || '';
    }
    const gap = this.querySelector('[data-td-cmp-diffprice]');
    if (gap) {
      const mine = Number(this.dataset.thisPrice || 0);
      gap.replaceChildren();
      if (mine > 0 && data.price > 0) {
        const difference = data.price - mine;
        const amount = formatMoney(Math.abs(difference), this.dataset.moneyFormat || '', this.dataset.trim === 'true');
        const text = difference < 0 ? this.dataset.tCheaper : difference > 0 ? this.dataset.tDearer : this.dataset.tSame;
        const span = document.createElement('span');
        if (difference < 0) span.className = 'td-cmp__diff--less';
        span.textContent = (text || '').replace('[a]', amount);
        gap.append(span);
      }
    }

    for (const row of this.querySelectorAll('[data-td-cmp-row]')) {
      if (!(row instanceof HTMLElement) || row.hasAttribute('data-td-cmp-sized')) continue;
      const key = row.dataset.tdCmpRow || '';
      const cell = row.querySelector('[data-td-cmp-b]');
      const mine = row.querySelector('td:not([data-td-cmp-b])');
      const value = (data.specs && data.specs[key]) || '';
      if (cell) cell.textContent = value;
      row.toggleAttribute('data-same', (mine?.textContent || '').trim() === value.trim());
    }
  }
}

/**
 * Scrolls the page by a number of pixels, whether the window or Horizon's page wrapper scrolls.
 * @param {number} delta
 */
function scrollPageBy(delta) {
  if (!delta) return;
  const before = window.scrollY;
  window.scrollBy(0, delta);
  if (window.scrollY !== before) return;
  const wrapper = document.querySelector('.page-wrapper');
  if (wrapper instanceof HTMLElement && wrapper.scrollHeight > wrapper.clientHeight) wrapper.scrollTop += delta;
}

const SIM_SCROLL_KEY = 'td-sim-switch';

/**
 * The SIM versions of a model are separate products. Choosing another version opens that product;
 * the page is fetched as soon as the shopper points at it, and the new page opens at the same spot
 * with the chosen version marked, so switching feels like changing any other option.
 */
class TdSim extends HTMLElement {
  connectedCallback() {
    this.addEventListener('pointerover', this.#prefetch);
    this.addEventListener('focusin', this.#prefetch);
    this.addEventListener('touchstart', this.#prefetch, { passive: true });
    this.addEventListener('click', this.#onClick);
    window.addEventListener('pageshow', this.#onPageShow);
    this.#restore();
  }

  disconnectedCallback() {
    this.removeEventListener('pointerover', this.#prefetch);
    this.removeEventListener('focusin', this.#prefetch);
    this.removeEventListener('touchstart', this.#prefetch);
    this.removeEventListener('click', this.#onClick);
    window.removeEventListener('pageshow', this.#onPageShow);
  }

  /** Coming back to this page from the browser's history: nothing is loading any more */
  #onPageShow = () => {
    this.removeAttribute('aria-busy');
    for (const link of this.querySelectorAll('.is-loading')) link.classList.remove('is-loading');
  };

  /** @param {Event} event */
  #prefetch = (event) => {
    const link = event.target instanceof Element ? event.target.closest('a[data-td-sim-link]') : null;
    if (!(link instanceof HTMLAnchorElement) || link.dataset.tdPrefetched) return;
    link.dataset.tdPrefetched = 'true';
    const hint = document.createElement('link');
    hint.rel = 'prefetch';
    hint.href = link.href;
    document.head.append(hint);
  };

  /**
   * Phones show one line with the current version until the shopper asks to change it (CSS hides the
   * versions while .td-sim is not .is-open); computers always show the versions.
   * @param {boolean} focus
   */
  #open(focus) {
    const root = this.closest('.td-sim');
    if (!root || root.classList.contains('is-open')) return;
    root.classList.add('is-open');
    this.querySelector('[data-td-sim-open]')?.setAttribute('aria-expanded', 'true');
    const fieldset = this.querySelector('fieldset');
    if (focus && fieldset instanceof HTMLElement) fieldset.focus({ preventScroll: true });
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const opener = event.target instanceof Element ? event.target.closest('[data-td-sim-open]') : null;
    if (opener) {
      this.#open(true);
      return;
    }
    const link = event.target instanceof Element ? event.target.closest('a[data-td-sim-link]') : null;
    if (!(link instanceof HTMLAnchorElement)) return;
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    try {
      const path = new URL(link.href, window.location.href).pathname;
      sessionStorage.setItem(SIM_SCROLL_KEY, JSON.stringify({ top: this.getBoundingClientRect().top, at: Date.now(), path }));
    } catch (error) {
      /* storage unavailable: the page simply opens at the top */
    }
    link.classList.add('is-loading');
    this.setAttribute('aria-busy', 'true');
  };

  #restore() {
    /** @type {{top: number, at: number, path: string} | null} */
    let saved = null;
    try {
      saved = JSON.parse(sessionStorage.getItem(SIM_SCROLL_KEY) || 'null');
      if (saved) sessionStorage.removeItem(SIM_SCROLL_KEY);
    } catch (error) {
      saved = null;
    }
    if (!saved || Date.now() - saved.at > 20000 || saved.path !== window.location.pathname) return;
    // The shopper switched versions here: keep the versions open on phones, where they tapped
    this.#open(false);
    requestAnimationFrame(() => {
      scrollPageBy(Math.round(this.getBoundingClientRect().top - saved.top));
      this.closest('.td-sim')?.classList.add('td-sim--arrived');
    });
  }
}

/**
 * Home page condition grades on phones and tablets (18 Oct 2026): one grade at a time, chosen with tabs
 * above the card (sections/td-grades.liquid). From 900 px the grades sit side by side, the tabs are
 * hidden, and the tab roles come off so screen readers meet three plain articles.
 */
class TdGradeTabs extends HTMLElement {
  #media = window.matchMedia('(max-width: 899px)');
  /** @type {HTMLElement[]} */
  #tabs = [];
  /** @type {HTMLElement[]} */
  #panels = [];
  #index = 0;

  connectedCallback() {
    this.#tabs = Array.from(this.querySelectorAll('[data-td-grade-tab]'));
    this.#panels = Array.from(this.querySelectorAll('[data-td-grade-panel]'));
    if (this.#tabs.length < 2) return;
    this.#index = Math.max(0, this.#tabs.findIndex((tab) => tab.classList.contains('is-on')));
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKey);
    this.#media.addEventListener('change', this.#apply);
    this.#apply();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKey);
    this.#media.removeEventListener('change', this.#apply);
  }

  #apply = () => {
    const tabbed = this.#media.matches;
    const list = this.querySelector('[data-td-grade-tablist]');
    if (list) {
      if (tabbed) {
        list.setAttribute('role', 'tablist');
        list.setAttribute('aria-label', this.dataset.label || '');
      } else {
        list.removeAttribute('role');
        list.removeAttribute('aria-label');
      }
    }
    this.#tabs.forEach((tab, index) => {
      const on = index === this.#index;
      tab.classList.toggle('is-on', on);
      if (tabbed) {
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-selected', String(on));
        tab.tabIndex = on ? 0 : -1;
      } else {
        tab.removeAttribute('role');
        tab.removeAttribute('aria-selected');
        tab.removeAttribute('tabindex');
      }
    });
    this.#panels.forEach((panel, index) => {
      panel.classList.toggle('is-off', index !== this.#index);
      if (tabbed) {
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', this.#tabs[index]?.id || '');
        panel.tabIndex = 0;
      } else {
        panel.removeAttribute('role');
        panel.removeAttribute('aria-labelledby');
        panel.removeAttribute('tabindex');
      }
    });
  };

  /**
   * @param {number} index
   * @param {boolean} focus
   */
  #select(index, focus) {
    if (index < 0 || index >= this.#tabs.length) return;
    this.#index = index;
    this.#apply();
    if (focus) this.#tabs[index]?.focus();
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const tab = event.target instanceof Element ? event.target.closest('[data-td-grade-tab]') : null;
    if (!(tab instanceof HTMLElement)) return;
    this.#select(this.#tabs.indexOf(tab), false);
  };

  /**
   * Arrow keys move between the tabs (mirrored in Arabic), Home and End go to the first and last.
   * @param {KeyboardEvent} event
   */
  #onKey = (event) => {
    if (!this.#media.matches) return;
    const tab = event.target instanceof Element ? event.target.closest('[data-td-grade-tab]') : null;
    if (!(tab instanceof HTMLElement)) return;
    const last = this.#tabs.length - 1;
    const step = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
    let next = this.#tabs.indexOf(tab);
    if (event.key === 'ArrowRight') next += step;
    else if (event.key === 'ArrowLeft') next -= step;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = last;
    else return;
    event.preventDefault();
    if (next < 0) next = last;
    if (next > last) next = 0;
    this.#select(next, true);
  };
}

if (!customElements.get('td-refresh')) customElements.define('td-refresh', TdRefresh);
if (!customElements.get('td-buy-now')) customElements.define('td-buy-now', TdBuyNow);
if (!customElements.get('td-sim')) customElements.define('td-sim', TdSim);
if (!customElements.get('td-delivery')) customElements.define('td-delivery', TdDelivery);
if (!customElements.get('td-toast')) customElements.define('td-toast', TdToast);
if (!customElements.get('td-slideshow')) customElements.define('td-slideshow', TdSlideshow);
if (!customElements.get('td-reviews')) customElements.define('td-reviews', TdReviews);
if (!customElements.get('td-check-cards')) customElements.define('td-check-cards', TdCheckCards);
if (!customElements.get('td-holo')) customElements.define('td-holo', TdHolo);
if (!customElements.get('td-turns')) customElements.define('td-turns', TdTurns);
if (!customElements.get('td-rail-box')) customElements.define('td-rail-box', TdRailBox);
if (!customElements.get('td-product-tabs')) customElements.define('td-product-tabs', TdProductTabs);
if (!customElements.get('td-size')) customElements.define('td-size', TdSize);
if (!customElements.get('td-compare')) customElements.define('td-compare', TdCompare);
if (!customElements.get('td-grade-tabs')) customElements.define('td-grade-tabs', TdGradeTabs);

if (document.readyState === 'loading') {
  document.addEventListener(
    'DOMContentLoaded',
    () => {
      initReveal();
      initStrikes();
      hideTradeinIfDeclined();
      watchBuyBar();
      openPanelFromHash();
      updateSummaries();
      prefetchSheetsWhenIdle();
    },
    { once: true }
  );
} else {
  initReveal();
  initStrikes();
  hideTradeinIfDeclined();
  watchBuyBar();
  openPanelFromHash();
  updateSummaries();
  prefetchSheetsWhenIdle();
}
window.addEventListener('pageshow', updateSummaries);

window.addEventListener('hashchange', openPanelFromHash);

document.addEventListener('shopify:section:load', (event) => {
  initReveal();
  if (event.target instanceof Element) initStrikes(event.target);
});
